import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ArrowUpDown, Check, Copy, ExternalLink, FileText, Plus, Printer, ShoppingCart, Trash2, Wallet, X } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import QBDealPicker, { type QBDealOption } from '../components/sales-activity/QBDealPicker';
import ApprovalMap, { approvalStepsForPurchaseDisplay } from '../components/purchasing/ApprovalMap';
import PurchaseApprovalBreadcrumb from '../components/purchasing/PurchaseApprovalBreadcrumb';
import PurchasePoForkBadge from '../components/purchasing/PurchasePoForkBadge';
import ProjectCardBalanceCheck from '../components/purchasing/ProjectCardBalanceCheck';
import { PurchaseStatHover } from '../components/purchasing/PurchaseStatHover';
import PurchaseQbBillIndicator from '../components/purchasing/PurchaseQbBillIndicator';
import PurchaseSearchOverlay from '../components/purchasing/PurchaseSearchOverlay';
import { useQbExpensesProjectPL } from '../hooks/useQbExpensesProjectPL';
import { resolvePurchaseQbBillFields } from '../utils/purchaseQbBillResolve';
import PurchaseBudgetsModal from '../components/purchasing/PurchaseBudgetsModal';
import PurchaseLineItemsEditor from '../components/purchasing/PurchaseLineItemsEditor';
import { printPurchaseChecklist, PurchasePrintSheet } from '../components/purchasing/PurchasePrintSheet';
import {
  purchaseOrderNumbers,
  purchaseVendorPoLabel,
  rewriteUniformVendorLinesOnHeaderChange,
  vendorGroupKeysChanged,
} from '../utils/purchaseLineVendors';
import PurchaseLinksEditor from '../components/purchasing/PurchaseLinksEditor';
import PurchaseLinkedText from '../components/purchasing/PurchaseLinkedText';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useAuth } from '../utils/AuthContext';
import { isEditableTarget, isHubQuickSearchGloballyBlocked } from '../utils/isEditableTarget';
import { storage } from '../utils/firebase';
import { useCRM } from '../utils/CRMContext';
import {
  buildPurchaseAttachmentDocumentId,
  PURCHASING_VIEWER_LEAD_ID,
} from '../utils/purchasingAttachmentViewer';
import { buildQBLinkDealOptions, parseQBLinkTargetId } from '../utils/qbDealLinkOptions';
import {
  canAccessPurchasing,
  canCancelPurchaseRequisition,
  canDeletePurchaseRequisition,
  canManagePurchaseSupportingAttachments,
  canMarkPurchasePurchased,
  canOverridePurchaseApproval,
  canRemovePurchaseReceipt,
  canEditPurchasedPurchaseAmounts,
  canReviseIssuedPurchaseAmount,
  canUploadPurchaseReceipt,
  canReviewPurchaseRequisition,
  canViewAllPurchaseRequisitions,
  canViewPurchaseBudgets,
  canReassignPurchaseRequester,
  type PurchasingActorAccess,
} from '../utils/purchasingAccess';
import { canWriteFinancesModule, resolveEmployeeDesignations } from '../utils/contractorPayablesAccess';
import { useBillingPayablesAccess } from '../hooks/useBillingPayablesAccess';
import { requesterHoldMessage } from '../utils/purchasingHolds';
import {
  agentDraftAlreadyInApproval,
  canReturnAgentCreatedPurchase,
  canReturnIssuedPurchaseToRequester,
  isAwaitingReviewer,
  matchesPurchaseStatusFilter,
  purchaseListStatus,
  type PurchaseStatusFilter,
} from '../utils/purchasingStatus';
import {
  canActOnPendingPurchaseStep,
  crmUsersToResolverUsers,
  isSelfAuthorizedPurchase,
  overlaySignedInApprover,
  purchaseApprovalUsesSubstitutedActor,
  resolveApprovalChain,
} from '../utils/purchasingApprovals';
import {
  defaultSelfIssuePaymentMethod,
  isAchPurchasePayment,
  parsePurchasingPolicy,
  resolvePaymentMethodId,
  withProjectCardPaymentMethod,
} from '../utils/purchasingPolicy';
import {
  billPaymentPurchaseHasUiClosePath,
  billPaymentPurchaseUiGuide,
  purchaseFollowsSupplierBill,
} from '../utils/purchaseOrderIssue';
import { canTransitionSupplierBill, type SupplierBill } from '../utils/supplierBills';
import {
  findSupplierBillForPurchaseOrder,
  updateSupplierBillStatus,
} from '../utils/supplierBillsService';
import { retrySupplierBillQboBill } from '../utils/retrySupplierBillQboBill';
import { purchaseListApprovalFollowOn } from '../utils/purchaseListApprovalTrail';
import {
  purchaseDisplayedPurchasedAt,
  purchasePurchaserDisplayName,
  purchaseRequestedByHeaderLabel,
  purchaseRequesterDirectoryName,
  purchaseRequesterDisplayName,
  purchaseRequesterOptionLabel,
} from '../utils/purchaseRequesterDisplay';
import { subscribePurchasingPolicy } from '../utils/purchasingPolicySettings';
import {
  approvePurchaseRequisition,
  cancelPurchaseRequisition,
  deletePurchaseRequisition,
  holdPurchaseRequisition,
  addPurchaseReceipts,
  addPurchaseSupportingAttachments,
  markPurchaseRequisitionPurchased,
  markSupplierPurchaseOrderPurchased,
  hasPurchaseReceiptProof,
  purchaseReceipts,
  purchaseSupportingFiles,
  removePurchaseAttachment,
  requestPurchaseActualVariance,
  releasePurchaseHold,
  reassignPurchaseRequester,
  assignPurchaseForecastLine,
  resubmitPurchaseRequisition,
  reviseIssuedPurchaseAmount,
  editPurchasedPurchaseAmounts,
  subscribeActiveOverheadForecastLines,
  returnPurchaseRequisition,
  submitPurchaseRequisition,
  subscribePurchaseInternalReview,
  subscribePurchaseRequisition,
  subscribePurchaseRequisitions,
} from '../utils/purchasingRequisitions';
import {
  buildPurchaseCostObject,
  CAPEX_THRESHOLD_USD,
  formatCostObjectLabel,
  PURCHASE_JOB_PL_BUCKETS,
  PURCHASE_OVERHEAD_GROUPS,
  PURCHASE_SPEND_CLASS_LABELS,
  resolveSpendClass,
  validatePurchaseCostObject,
} from '../utils/purchasingSpendClass';
import {
  emptyOpexBudgets,
  groupBudgetEnvelope,
  opexBudgetIntervalMeta,
  subscribeOpexBudgets,
  type OpexBudgets,
} from '../utils/opexBudgets';
import {
  canSelfConfirmPurchaseActual,
  formatPurchaseActualVariance,
  parsePurchaseUsdInput,
  purchaseActualWithinTolerance,
  resolvePurchaseActualAuthority,
} from '../utils/purchaseActualAmount';
import {
  approvedPurchaseAmountBaseline,
  canSubmitIssuedPurchaseRevise,
  formatIssuedPurchaseAmountHint,
  issuedPurchaseAmountEditKind,
} from '../utils/purchaseApprovedAmount';
import {
  formatPurchasedAmountEditHint,
  purchasedAmountEditKind,
} from '../utils/purchasedAmountEdit';
import { proposalDealIdForBudget, purchaseIsOverBudget, summarizeJobNode, summarizeOpexGroupMonth } from '../utils/purchaseBudget';
import { issuedSupplierPurchaseOrders } from '../utils/purchaseOrderPurchases';
import {
  sortPurchaseListRows,
  type PurchaseListSortDir,
  type PurchaseListSortKey,
} from '../utils/purchasingListSort';
import { useJobPurchaseBudget } from '../hooks/useJobPurchaseBudget';
import type { EnhancedDeal, TimberProject } from '../types';
import {
  draftsToLineItems,
  emptyPurchaseLineDraft,
  lineItemsToDrafts,
  requisitionTotal,
} from '../utils/purchasingLineItems';
import {
  linksToDrafts,
  additionalPurchaseLinks,
  preparedPurchaseLinks,
  type PurchaseLinkDraft,
} from '../utils/purchasingLinks';
import {
  clearPurchaseRequisitionDraft,
  hasPurchaseRequisitionDraft,
  loadPurchaseRequisitionDraft,
  savePurchaseRequisitionDraft,
} from '../utils/purchaseRequisitionDraft';
import type {
  PurchaseAttachment,
  PurchaseCostObject,
  PurchaseHoldInternal,
  PurchaseJobPlBucket,
  PurchaseOverheadGroup,
  PurchaseRequisition,
  PurchaseSpendClass,
  PurchasingPolicy,
} from '../types/purchasing';
import '../components/pm/pm-shared.css';
import './FinancesHub.css';
import './BillingPayablesPage.css';
import './PurchasingPage.css';

const PdfViewerModal = lazy(() => import('../components/pdf/PdfViewerModal'));
const ImageViewerModal = lazy(() => import('../components/pdf/ImageViewerModal'));

type ScopeFilter = 'mine' | 'awaiting' | 'all';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function budgetedCardValue(summary: { budgetUnset: boolean; budgetedUsd: number | null; interval: 'monthly' | 'quarterly' | 'annual' }) {
  if (summary.budgetUnset || summary.budgetedUsd == null) return 'Not set';
  return `${money(summary.budgetedUsd)} ${opexBudgetIntervalMeta(summary.interval).suffix}`;
}

function jobBudgetCardTitle(bucket?: PurchaseJobPlBucket | ''): string {
  return bucket === 'timber' ? 'Other materials remaining' : 'Proposal node remaining';
}

function timberForDeal(timberProjects: TimberProject[], dealId: string) {
  const matches = timberProjects.filter(p => p.dealId === dealId && p.status !== 'completed' && p.id);
  return matches.find(p => p.projectManager) || matches[0] || null;
}

const PurchasingPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers, enhancedDeals, timberProjects } = useCRM();
  const [searchParams, setSearchParams] = useSearchParams();
  const [policy, setPolicy] = useState<PurchasingPolicy>(() => parsePurchasingPolicy(undefined));
  const [rows, setRows] = useState<PurchaseRequisition[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<PurchaseStatusFilter>('all');
  const [composerOpen, setComposerOpen] = useState(false);
  const [draftAvailable, setDraftAvailable] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('pr'));
  const [deepLinkedRow, setDeepLinkedRow] = useState<PurchaseRequisition | null>(null);
  const [deepLinkDenied, setDeepLinkDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Load/subscribe failures only — must not blank the list for modal validation. */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Action failures shown without replacing the requisition list. */
  const [actionError, setActionError] = useState<string | null>(null);
  const [opexBudgets, setOpexBudgets] = useState<OpexBudgets>(() => emptyOpexBudgets());
  const [sortKey, setSortKey] = useState<PurchaseListSortKey>('po');
  const [sortDir, setSortDir] = useState<PurchaseListSortDir>('desc');
  const [budgetsOpen, setBudgetsOpen] = useState(false);

  const me = crmUsers.find(u => u.uid === user?.id);
  const designations = resolveEmployeeDesignations(user, crmUsers);
  const briefingProfile = useMemo(() => {
    const fromAuth = user?.executiveBriefing;
    const fromCrm = me?.executiveBriefing;
    const cfg = fromAuth?.enabled ? fromAuth : fromCrm?.enabled ? fromCrm : fromAuth || fromCrm;
    if (!cfg) return undefined;
    // User Management defaults an enabled briefing with no profile to COO.
    if (cfg.enabled && (!cfg.profile || cfg.profile === 'coo')) return 'coo' as const;
    return cfg.profile || undefined;
  }, [me?.executiveBriefing, user?.executiveBriefing]);
  const accessArgs = {
    authRole: user?.role,
    designations,
    briefingProfile,
  };
  const viewAll = canViewAllPurchaseRequisitions(accessArgs);
  const canOverride = canOverridePurchaseApproval(accessArgs);
  const canViewBudgets = canViewPurchaseBudgets(accessArgs);
  const showFinancesBudgetLink = canWriteFinancesModule({
    authRole: user?.role,
    designations,
  });

  useEffect(() => subscribePurchasingPolicy(setPolicy), []);
  useEffect(() => subscribeOpexBudgets(setOpexBudgets), []);

  useEffect(() => {
    if (!user?.id) return;
    return subscribePurchaseRequisitions(
      { userId: user.id, viewAll },
      next => {
        setRows(next);
        setLoaded(true);
        setLoadError(null);
      },
      err => {
        console.error('[PurchasingPage] purchase requisitions listen failed', err);
        setLoaded(true);
        setLoadError('Purchase orders could not be loaded. Refresh and try again.');
      },
    );
  }, [user?.id, viewAll]);

  useEffect(() => {
    const pr = searchParams.get('pr');
    if (pr) setSelectedId(pr);
    else {
      setDeepLinkedRow(null);
      setDeepLinkDenied(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('budgets') === '1' && canViewBudgets) {
      setBudgetsOpen(true);
    }
  }, [searchParams, canViewBudgets]);

  const openBudgets = () => {
    setBudgetsOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set('budgets', '1');
    setSearchParams(next, { replace: true });
  };

  const closeBudgets = () => {
    setBudgetsOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete('budgets');
    setSearchParams(next, { replace: true });
  };

  const openPurchaseFromBudgets = (id: string) => {
    setBudgetsOpen(false);
    setSelectedId(id);
    const next = new URLSearchParams(searchParams);
    next.delete('budgets');
    next.set('pr', id);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!selectedId || !user?.id) {
      setDeepLinkedRow(null);
      setDeepLinkDenied(false);
      return;
    }
    if (rows.some(r => r.id === selectedId)) {
      setDeepLinkedRow(null);
      setDeepLinkDenied(false);
      return;
    }
    if (!loaded) return;

    setDeepLinkDenied(false);
    return subscribePurchaseRequisition(
      selectedId,
      row => {
        setDeepLinkedRow(row);
        setDeepLinkDenied(row == null);
      },
      () => {
        setDeepLinkedRow(null);
        setDeepLinkDenied(true);
      },
    );
  }, [selectedId, rows, loaded, user?.id]);

  const dealOptions: QBDealOption[] = useMemo(
    () =>
      buildQBLinkDealOptions(enhancedDeals, timberProjects).map(o => ({
        id: o.id,
        label: o.label,
        searchText: o.searchText,
      })),
    [enhancedDeals, timberProjects],
  );

  const resolverUsers = useMemo(
    () => overlaySignedInApprover(
      crmUsersToResolverUsers(crmUsers),
      user?.id
        ? {
            uid: user.id,
            displayName: user.name || user.email || 'Unnamed',
            title: me?.title,
            isActive: me?.isActive,
            role: user.role || me?.role,
            reportsToUserId: me?.reportsToUserId || user.reportsToUserId,
            designations,
            executiveBriefing: user.executiveBriefing || me?.executiveBriefing,
          }
        : null,
    ),
    [crmUsers, designations, me, user],
  );

  const awaitingMe = useMemo(() => rows.filter(r => isAwaitingReviewer(r, user?.id)), [rows, user?.id]);


  const visible = useMemo(() => {
    const filtered = rows.filter(r => {
      if (scope === 'mine' && r.requesterId !== user?.id) return false;
      if (scope === 'awaiting' && !isAwaitingReviewer(r, user?.id)) return false;
      return matchesPurchaseStatusFilter(r, statusFilter);
    });
    return sortPurchaseListRows(filtered, sortKey, sortDir);
  }, [rows, scope, statusFilter, user?.id, sortKey, sortDir]);

  const toggleSort = (key: PurchaseListSortKey) => {
    if (sortKey === key) {
      setSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'items' || key === 'cost' || key === 'gate' || key === 'status' ? 'asc' : 'desc');
  };

  const selected = rows.find(r => r.id === selectedId) || deepLinkedRow;

  const openSelected = (id: string | null) => {
    setSelectedId(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('pr', id);
    else next.delete('pr');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' || e.repeat || e.altKey || e.ctrlKey || e.metaKey) return;
      if (searchOpen || composerOpen) return;
      if (isEditableTarget(e.target) || isHubQuickSearchGloballyBlocked()) return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [searchOpen, composerOpen]);

  useEffect(() => {
    if (!user?.id || composerOpen) return;
    setDraftAvailable(hasPurchaseRequisitionDraft(user.id));
  }, [user?.id, composerOpen]);

  if (!user) return <LoadingScreen />;
  if (!canAccessPurchasing(user.role)) return <Navigate to="/" replace />;

  const Header = AppHeader;

  return (
    <div className="fin-hub po-hub">
      <Header />
      <div className="bp-fin-main">
        <div className="fin-hub-toolbar bp-toolbar">
          <div className="bp-toolbar-main">
            <h1 className="fin-hub-title">
              <ShoppingCart size={18} aria-hidden style={{ marginRight: 8, verticalAlign: '-3px' }} />
              Purchasing
            </h1>
            <div className="bp-toolbar-stats">
              <span className="fin-stat">
                <span className="fin-stat-strong">{awaitingMe.length}</span> awaiting you
              </span>
              <PurchaseStatHover kind="issued" rows={rows}>
                <span className="fin-stat po-stat-hoverable" tabIndex={0}>
                  <span className="fin-stat-strong">{rows.filter(r => r.status === 'approved' && !r.hold).length}</span> POs issued
                </span>
              </PurchaseStatHover>
              <PurchaseStatHover kind="purchased" rows={rows}>
                <span className="fin-stat po-stat-hoverable" tabIndex={0}>
                  <span className="fin-stat-strong">{rows.filter(r => r.status === 'purchased').length}</span> purchased
                </span>
              </PurchaseStatHover>
            </div>
          </div>
          <div className="po-toolbar-actions">
            {canViewBudgets && (
              <button type="button" className="fin-secondary-btn" onClick={openBudgets}>
                <Wallet size={14} aria-hidden /> Budgets
              </button>
            )}
            <button type="button" className="fin-primary-btn" onClick={() => setComposerOpen(true)}>
              <Plus size={14} aria-hidden /> New requisition
            </button>
          </div>
        </div>

        {draftAvailable && !composerOpen && (
          <div className="po-draft-banner" role="status">
            <p className="po-draft-banner-text">
              You have an unfinished purchase requisition draft. It was saved on this device so you can
              keep editing.
            </p>
            <div className="po-draft-banner-actions">
              <button type="button" className="fin-primary-btn" onClick={() => setComposerOpen(true)}>
                Resume draft
              </button>
              <button
                type="button"
                className="fin-secondary-btn"
                onClick={() => {
                  clearPurchaseRequisitionDraft(user.id);
                  setDraftAvailable(false);
                }}
              >
                Discard
              </button>
            </div>
          </div>
        )}

        <div className="fin-hub-content po-content">
          <div className="po-filters">
            {([
              ['all', 'All'],
              ['awaiting', 'Awaiting me'],
              ['mine', 'Mine'],
            ] as Array<[ScopeFilter, string]>).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`fin-pill ${scope === id ? 'po-pill-active' : ''}`}
                onClick={() => setScope(id)}
              >
                {label}
              </button>
            ))}
            <span className="po-filter-gap" />
            {([
              ['open', 'Open'],
              ['approved', 'PO issued'],
              ['purchased', 'Purchased'],
              ['closed', 'Closed'],
              ['active', 'Active'],
              ['all', 'Every Status'],
            ] as Array<[PurchaseStatusFilter, string]>).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`fin-pill ${statusFilter === id ? 'po-pill-active' : ''}`}
                onClick={() => setStatusFilter(id)}
              >
                {label}
              </button>
            ))}
            <span className="po-search-launch">
              <kbd>Space</kbd> to search
            </span>
          </div>

          {deepLinkDenied && selectedId && (
            <div className="po-deeplink-banner" role="alert">
              <p>
                Purchase requisition <code>{selectedId}</code> could not be opened — it may have been
                deleted, or you may not have access.
              </p>
              <button type="button" className="fin-secondary-btn" onClick={() => openSelected(null)}>
                Dismiss link
              </button>
            </div>
          )}

          {actionError && !selected && (
            <div className="po-deeplink-banner" role="alert">
              <p className="po-error">{actionError}</p>
              <button type="button" className="fin-secondary-btn" onClick={() => setActionError(null)}>
                Dismiss
              </button>
            </div>
          )}

          {!loaded ? (
            <div className="fin-hub-loading">
              <div className="fin-hub-loading-bar" />
            </div>
          ) : loadError ? (
            <div className="po-empty">
              <p className="po-error">{loadError}</p>
            </div>
          ) : deepLinkDenied && selectedId && visible.length === 0 ? (
            <div className="po-empty">
              <p className="po-error">
                This purchase requisition link is invalid or the record is gone. Clear the link to
                continue in Purchasing.
              </p>
              <button type="button" className="fin-secondary-btn" onClick={() => openSelected(null)}>
                Clear link
              </button>
            </div>
          ) : visible.length === 0 ? (
            <div className="po-empty">
              {rows.length > 0 ? (
                <>
                  <p>
                    {rows.length === 1
                      ? '1 requisition is hidden by these filters.'
                      : `${rows.length} requisitions are hidden by these filters.`}
                  </p>
                  <button
                    type="button"
                    className="fin-primary-btn"
                    onClick={() => {
                      setScope(viewAll ? 'all' : 'mine');
                      setStatusFilter('all');
                    }}
                  >
                    Show all
                  </button>
                </>
              ) : (
                <>
                  <p>No purchase requisitions in this view.</p>
                  <button type="button" className="fin-primary-btn" onClick={() => setComposerOpen(true)}>
                    Create a requisition
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="bp-table-scroll">
              <table className="bp-data-table">
                <thead>
                  <tr>
                    {([
                      ['pr', 'PR'],
                      ['items', 'Items'],
                      ['amount', 'Amount'],
                      ['cost', 'Cost object'],
                      ['gate', 'Progress'],
                      ['status', 'Status'],
                      ['po', 'PO'],
                    ] as Array<[PurchaseListSortKey, string]>).map(([key, label]) => (
                      <th key={key} className={key === 'amount' ? 'bp-num' : undefined}>
                        <button
                          type="button"
                          className={`fin-th-sort${key === 'amount' ? ' right' : ''}${sortKey === key ? ' active' : ''}`}
                          onClick={() => toggleSort(key)}
                          aria-label={`Sort by ${label}${sortKey === key ? `, ${sortDir === 'asc' ? 'ascending' : 'descending'}` : ''}`}
                        >
                          {label}
                          <span
                            className={`fin-th-sort-arrow${sortKey === key && sortDir === 'desc' ? ' desc' : ''}${sortKey === key ? ' on' : ''}`}
                            aria-hidden
                          >
                            <ArrowUpDown size={10} />
                          </span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map(row => {
                    const listStatus = purchaseListStatus(row);
                    const listAmount =
                      row.status === 'purchased' && row.purchasedAmount != null ? row.purchasedAmount : row.amount;
                    const listFollowOn = purchaseListApprovalFollowOn(row, timberProjects, enhancedDeals);
                    const vendorPoLabel = purchaseVendorPoLabel(row.lineItems, row.vendorName, row.poNumber);
                    const poLabel = purchaseOrderNumbers(row).join(', ');
                    return (
                      <tr key={row.id} className="po-row" onClick={() => openSelected(row.id)}>
                        <td className="bp-cell-project">{row.prNumber}</td>
                        <td>
                          <span className="bp-item-label">{row.title}</span>
                          <span className="bp-item-detail">
                            {row.lineItems.length > 1 ? `${row.lineItems.length} lines` : '1 line'}
                            {vendorPoLabel ? ` · ${vendorPoLabel}` : ''}
                          </span>
                        </td>
                        <td className="bp-num">
                          {money(listAmount)}
                          {row.status === 'purchased' &&
                          row.purchasedAmount != null &&
                          Math.abs(row.purchasedAmount - row.amount) > 0.005 ? (
                            <span className="bp-item-detail">PO {money(row.amount)}</span>
                          ) : null}
                        </td>
                        <td>{formatCostObjectLabel(row.costObject)}</td>
                        <td className="po-trail-cell">
                          <span className="po-trail-with-fork">
                            <PurchaseApprovalBreadcrumb
                              steps={approvalStepsForPurchaseDisplay(row.approvalSteps, row)}
                              origin={{
                                userName: purchaseRequesterDisplayName(row, crmUsers),
                                at: row.createdAt,
                              }}
                              followOn={listFollowOn}
                              variance={row.status === 'approved' ? row.purchaseVariance : undefined}
                            />
                            <PurchasePoForkBadge
                              prNumber={row.prNumber}
                              lines={row.lineItems}
                              headerVendor={row.vendorName}
                              headerPo={row.poNumber}
                              salesTaxPct={row.salesTaxPct}
                            />
                          </span>
                        </td>
                        <td>
                          <span className={`fin-pill po-status po-status--${listStatus.id}`}>{listStatus.label}</span>
                        </td>
                        <td className="bp-cell-project">{poLabel || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {searchOpen && (
        <PurchaseSearchOverlay
          rows={rows}
          onClose={() => setSearchOpen(false)}
          onOpen={(id) => openSelected(id)}
        />
      )}

      {budgetsOpen && canViewBudgets && (
        <PurchaseBudgetsModal
          rows={rows}
          opexBudgets={opexBudgets}
          dealOptions={dealOptions}
          showFinancesLink={showFinancesBudgetLink}
          onClose={closeBudgets}
          onOpenPurchase={openPurchaseFromBudgets}
        />
      )}

      {composerOpen && (
        <RequisitionComposer
          requesterId={user.id}
          requesterName={user.name || user.email || 'Employee'}
          draftOwnerId={user.id}
          crmUsers={crmUsers}
          canPickRequester={canOverride}
          policy={policy}
          resolverUsers={resolverUsers}
          dealOptions={dealOptions}
          timberProjects={timberProjects}
          rows={rows}
          opexBudgets={opexBudgets}
          canOverride={canOverride}
          canViewBudgets={canViewBudgets}
          onClose={() => setComposerOpen(false)}
          onCreated={(id, issued) => {
            clearPurchaseRequisitionDraft(user.id);
            setDraftAvailable(false);
            setComposerOpen(false);
            setScope('mine');
            setStatusFilter(issued ? 'approved' : 'open');
            openSelected(id);
          }}
        />
      )}

      {selected && (
        <PurchasePrintSheet
          row={selected}
          requesterName={purchaseRequestedByHeaderLabel(selected, crmUsers)}
        />
      )}

      {selected && (
        <RequisitionDetail
          row={selected}
          actorId={user.id}
          actorName={user.name || user.email || 'Employee'}
          accessArgs={accessArgs}
          canOverride={canOverride}
          canViewBudgets={canViewBudgets}
          crmUsers={crmUsers}
          policy={policy}
          resolverUsers={resolverUsers}
          dealOptions={dealOptions}
          timberProjects={timberProjects}
          enhancedDeals={enhancedDeals}
          rows={rows}
          opexBudgets={opexBudgets}
          busy={busy}
          error={actionError}
          onClose={() => {
            setActionError(null);
            openSelected(null);
          }}
          onBusy={setBusy}
          onError={setActionError}
        />
      )}
    </div>
  );
};

function costObjectFromSpendFields(
  spendClass: PurchaseSpendClass,
  overheadGroup: PurchaseOverheadGroup | '',
  jobPickerValue: string,
  dealOptions: QBDealOption[],
  timberProjects: TimberProject[],
  plBucket: PurchaseJobPlBucket | '' = '',
  overheadExpenseId = '',
): PurchaseCostObject {
  if (spendClass === 'job') {
    const { dealId } = parseQBLinkTargetId(jobPickerValue);
    const label = dealOptions.find(o => o.id === jobPickerValue)?.label || 'Job';
    const timber = dealId ? timberForDeal(timberProjects, dealId) : null;
    return buildPurchaseCostObject({
      spendClass: 'job',
      dealId: dealId || undefined,
      projectId: timber?.id,
      jobLabel: label,
      plBucket,
    });
  }
  if (spendClass === 'overhead') {
    return buildPurchaseCostObject({
      spendClass: 'overhead',
      overheadGroup,
      overheadExpenseId,
    });
  }
  return buildPurchaseCostObject({ spendClass: 'capex' });
}

function jobPickerValueFromCostObject(costObject: PurchaseCostObject): string {
  if (resolveSpendClass(costObject) !== 'job') return '';
  return costObject.dealId || '';
}

function BudgetCapCard({
  title,
  lines,
  over,
  note,
}: {
  title: string;
  lines: Array<{ label: string; value: string }>;
  over?: boolean;
  note?: string;
}) {
  return (
    <div className={`po-budget-card${over ? ' po-budget-card--over' : ''}`} aria-label={title}>
      <div className="po-envelope-title">{title}</div>
      <div className="po-budget-lines">
        {lines.map(line => (
          <div key={line.label}>
            <span>{line.label}</span>
            <strong>{line.value}</strong>
          </div>
        ))}
      </div>
      {note ? <p className="po-spend-hint">{note}</p> : null}
      {over ? <p className="po-budget-over">Over remaining budget.</p> : null}
    </div>
  );
}

function useOverheadForecastLines(enabled: boolean): Array<{ id: string; name: string }> {
  const [lines, setLines] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    if (!enabled) {
      setLines([]);
      return;
    }
    return subscribeActiveOverheadForecastLines(setLines, () => setLines([]));
  }, [enabled]);
  return lines;
}

function forecastCoverageNote(lineId: string, lines: Array<{ id: string; name: string }>): string | undefined {
  const id = lineId.trim();
  if (!id) return undefined;
  const name = lines.find(line => line.id === id)?.name || 'this forecast line';
  return `Covered by ${name}. Not drawn from this OpEx group budget.`;
}

function ForecastLineSelect({
  value,
  lines,
  disabled,
  onChange,
}: {
  value: string;
  lines: Array<{ id: string; name: string }>;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const known = lines.some(line => line.id === value);
  return (
    <label className="fin-modal-field full">
      <span className="fin-modal-label">Pays forecast line</span>
      <select
        className="fin-modal-select"
        value={value}
        disabled={disabled}
        aria-label="Pays forecast line"
        onChange={event => onChange(event.target.value)}
      >
        <option value="">Group budget only</option>
        {value && !known ? <option value={value}>Linked forecast line</option> : null}
        {lines.map(line => (
          <option key={line.id} value={line.id}>{line.name}</option>
        ))}
      </select>
      <p className="po-spend-hint">
        {value
          ? 'This PO pays that known charge and is not drawn from the OpEx group budget.'
          : 'Leave this blank to count the PO in the OpEx group budget.'}
      </p>
    </label>
  );
}

function SpendClassificationFields({
  spendClass,
  onSpendClassChange,
  overheadGroup,
  onOverheadGroupChange,
  jobPicker,
  onJobPickerChange,
  plBucket,
  onPlBucketChange,
  dealOptions,
  opexCard,
  jobCard,
  forecastLineField,
}: {
  spendClass: PurchaseSpendClass;
  onSpendClassChange: (value: PurchaseSpendClass) => void;
  overheadGroup: PurchaseOverheadGroup | '';
  onOverheadGroupChange: (value: PurchaseOverheadGroup | '') => void;
  jobPicker: string;
  onJobPickerChange: (value: string) => void;
  plBucket: PurchaseJobPlBucket | '';
  onPlBucketChange: (value: PurchaseJobPlBucket | '') => void;
  dealOptions: QBDealOption[];
  opexCard?: React.ReactNode;
  jobCard?: React.ReactNode;
  forecastLineField?: React.ReactNode;
}) {
  return (
    <>
      <label className="fin-modal-field">
        <span className="fin-modal-label">Spend class</span>
        <select
          className="fin-modal-select"
          value={spendClass}
          onChange={e => onSpendClassChange(e.target.value as PurchaseSpendClass)}
          aria-label="Spend class"
        >
          {(Object.keys(PURCHASE_SPEND_CLASS_LABELS) as PurchaseSpendClass[]).map(id => (
            <option key={id} value={id}>
              {PURCHASE_SPEND_CLASS_LABELS[id]}
            </option>
          ))}
        </select>
      </label>
      {spendClass === 'job' ? (
        <>
          <div className="fin-modal-field">
            <span className="fin-modal-label">Job / project</span>
            <QBDealPicker
              value={jobPicker}
              onChange={onJobPickerChange}
              dealOptions={dealOptions}
              emptyLabel="Choose a job"
              aria-label="Job or project"
            />
          </div>
          {jobPicker ? (
            <label className="fin-modal-field">
              <span className="fin-modal-label">Proposal node</span>
              <select
                className="fin-modal-select"
                value={plBucket}
                onChange={e => onPlBucketChange(e.target.value as PurchaseJobPlBucket | '')}
                aria-label="Proposal node"
              >
                <option value="">Select Materials, Install, Travel…</option>
                {PURCHASE_JOB_PL_BUCKETS.map(bucket => (
                  <option key={bucket.id} value={bucket.id}>
                    {bucket.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {jobCard}
        </>
      ) : null}
      {spendClass === 'overhead' ? (
        <>
          <label className="fin-modal-field">
            <span className="fin-modal-label">OpEx bucket</span>
            <select
              className="fin-modal-select"
              value={overheadGroup}
              onChange={e => onOverheadGroupChange(e.target.value as PurchaseOverheadGroup | '')}
              aria-label="OpEx bucket"
            >
              <option value="">Select a bucket</option>
              {PURCHASE_OVERHEAD_GROUPS.map(group => (
                <option key={group.id} value={group.id}>
                  {group.label}
                </option>
              ))}
            </select>
          </label>
          {forecastLineField}
          {opexCard}
        </>
      ) : null}
      {spendClass === 'capex' ? (
        <div className="fin-modal-field">
          <span className="fin-modal-label">CapEx</span>
          <p className="po-spend-hint">
            Capital spend (no job tag). Threshold placeholder:{' '}
            {CAPEX_THRESHOLD_USD == null ? 'not set' : money(CAPEX_THRESHOLD_USD)}.
          </p>
        </div>
      ) : null}
    </>
  );
}

function RequisitionComposer({
  requesterId,
  requesterName,
  draftOwnerId,
  crmUsers,
  canPickRequester,
  policy,
  resolverUsers,
  dealOptions,
  timberProjects,
  rows,
  opexBudgets,
  canOverride,
  canViewBudgets,
  onClose,
  onCreated,
}: {
  requesterId: string;
  requesterName: string;
  /** Signed-in user id used for localStorage draft key. */
  draftOwnerId: string;
  crmUsers: Array<{ uid: string; displayName: string; isActive?: boolean; role?: string }>;
  canPickRequester: boolean;
  policy: PurchasingPolicy;
  resolverUsers: ReturnType<typeof crmUsersToResolverUsers>;
  dealOptions: QBDealOption[];
  timberProjects: TimberProject[];
  rows: PurchaseRequisition[];
  opexBudgets: OpexBudgets;
  canOverride: boolean;
  canViewBudgets: boolean;
  onClose: () => void;
  onCreated: (id: string, issued: boolean) => void;
}) {
  const restoredDraft = useMemo(() => loadPurchaseRequisitionDraft(draftOwnerId), [draftOwnerId]);
  const [requesterUserId, setRequesterUserId] = useState(
    () => restoredDraft?.requesterUserId || requesterId,
  );
  const requesterOptions = useMemo(
    () =>
      crmUsers
        .filter(u => u.isActive !== false && u.role !== 'supplier' && u.role !== 'client')
        .slice()
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers],
  );
  const effectiveRequesterName = useMemo(
    () => purchaseRequesterDirectoryName(requesterUserId, crmUsers, requesterName),
    [crmUsers, requesterName, requesterUserId],
  );
  const [vendorName, setVendorName] = useState(() => restoredDraft?.vendorName ?? '');
  const [justification, setJustification] = useState(() => restoredDraft?.justification ?? '');
  const [spendClass, setSpendClass] = useState<PurchaseSpendClass>(
    () => restoredDraft?.spendClass ?? 'overhead',
  );
  const [overheadGroup, setOverheadGroup] = useState<PurchaseOverheadGroup | ''>(
    () => restoredDraft?.overheadGroup ?? '',
  );
  const [forecastLineId, setForecastLineId] = useState('');
  const forecastLines = useOverheadForecastLines(canViewBudgets && spendClass === 'overhead');
  const [plBucket, setPlBucket] = useState<PurchaseJobPlBucket | ''>(
    () => restoredDraft?.plBucket ?? '',
  );
  const [jobPicker, setJobPicker] = useState(() => restoredDraft?.jobPicker ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [lines, setLines] = useState(() =>
    restoredDraft?.lines?.length ? restoredDraft.lines : [emptyPurchaseLineDraft()],
  );
  const [links, setLinks] = useState<PurchaseLinkDraft[]>(() => restoredDraft?.links ?? []);
  const [paymentMethod, setPaymentMethod] = useState(
    () => restoredDraft?.paymentMethod || defaultSelfIssuePaymentMethod(policy.paymentMethods),
  );
  const [paymentNote, setPaymentNote] = useState(() => restoredDraft?.paymentNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoredNotice] = useState(() => !!restoredDraft);
  const issueMethods = withProjectCardPaymentMethod(policy.paymentMethods);

  const persistDraftNow = () => {
    savePurchaseRequisitionDraft(draftOwnerId, {
      requesterUserId,
      vendorName,
      justification,
      spendClass,
      overheadGroup,
      plBucket,
      jobPicker,
      lines,
      links,
      paymentMethod,
      paymentNote,
    });
  };

  useEffect(() => {
    const handle = window.setTimeout(() => {
      persistDraftNow();
    }, 400);
    return () => window.clearTimeout(handle);
    // Intentionally omit persistDraftNow — fields above are the draft inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draftOwnerId,
    requesterUserId,
    vendorName,
    justification,
    spendClass,
    overheadGroup,
    plBucket,
    jobPicker,
    lines,
    links,
    paymentMethod,
    paymentNote,
  ]);

  /** Close always flushes localStorage when any field/line has content; empty clears. */
  const closeAndPersistDraft = () => {
    persistDraftNow();
    onClose();
  };

  const lineItems = draftsToLineItems(lines);
  const parsedAmount = requisitionTotal(lineItems, policy.salesTaxPct);
  const costObject = costObjectFromSpendFields(
    spendClass,
    overheadGroup,
    jobPicker,
    dealOptions,
    timberProjects,
    plBucket,
    forecastLineId,
  );
  const budgetDealId = proposalDealIdForBudget(costObject.dealId, timberProjects);
  const timber = budgetDealId ? timberForDeal(timberProjects, budgetDealId) : null;
  const jobBudget = useJobPurchaseBudget(budgetDealId, plBucket);
  const opexSummary =
    spendClass === 'overhead' && overheadGroup
      ? summarizeOpexGroupMonth({
          rows,
          group: overheadGroup,
          budgetedUsd: groupBudgetEnvelope(opexBudgets, overheadGroup).amountUsd,
          interval: groupBudgetEnvelope(opexBudgets, overheadGroup).interval,
        })
      : null;
  const jobSummary =
    spendClass === 'job' && budgetDealId && plBucket
      ? summarizeJobNode({
          rows,
          dealId: budgetDealId,
          plBucket,
          budgetUsd: jobBudget.budgetUsd,
          actualUsd: jobBudget.actualUsd,
        })
      : null;
  const paysForecastLine = spendClass === 'overhead' && !!forecastLineId.trim();
  const overBudget = purchaseIsOverBudget({
    paysForecastLine,
    ...(opexSummary
      ? { opexRemainingUsd: opexSummary.remainingUsd }
      : jobSummary
        ? { jobRemainingUsd: jobSummary.remainingUsd, jobBudgetReady: jobBudget.loaded }
        : {}),
    amountUsd: parsedAmount,
  });
  const resolved = resolveApprovalChain({
    requesterId: requesterUserId,
    amount: parsedAmount,
    costType: costObject.type,
    spendClass,
    projectManagerUserId: timber?.projectManager,
    users: resolverUsers,
    policy,
  });
  const budgetBlocksSubmit = overBudget && !canOverride && !!resolved.selfAuthorized;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const classificationError = validatePurchaseCostObject(costObject);
      if (classificationError) throw new Error(classificationError);
      if (resolved.unresolved) throw new Error(resolved.error);
      if (budgetBlocksSubmit) {
        throw new Error('This amount is over the remaining budget.');
      }
      const id = await submitPurchaseRequisition({
        requesterId: requesterUserId,
        requesterName: effectiveRequesterName,
        justification,
        vendorName,
        lineItems,
        links: preparedPurchaseLinks(links),
        salesTaxPct: policy.salesTaxPct,
        costObject,
        steps: resolved.steps,
        bandLabel: resolved.bandLabel,
        files,
        issueNow: !!resolved.selfAuthorized,
        paymentMethod: resolved.selfAuthorized ? paymentMethod : undefined,
        paymentMethodId: resolved.selfAuthorized ? resolvePaymentMethodId(paymentMethod, issueMethods) : undefined,
        paymentMethodNote: resolved.selfAuthorized ? paymentNote : undefined,
      });
      clearPurchaseRequisitionDraft(draftOwnerId);
      onCreated(id, !!resolved.selfAuthorized);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit requisition.');
    } finally {
      setBusy(false);
    }
  };

  const discardDraftAndClose = () => {
    clearPurchaseRequisitionDraft(draftOwnerId);
    onClose();
  };

  return (
    <div className="fin-modal-overlay" onClick={closeAndPersistDraft}>
      <div className="fin-modal po-modal" onClick={e => e.stopPropagation()}>
        <div className="fin-modal-header">
          <h3>New purchase requisition</h3>
          <button
            type="button"
            className="fin-modal-close"
            onClick={closeAndPersistDraft}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="fin-modal-body">
          {restoredNotice && (
            <p className="po-draft-restored" role="status">
              Restored your unfinished draft from this device. Screenshot and PDF attachments are not
              saved in drafts — re-attach them before submitting if needed.
            </p>
          )}
          <div className="fin-modal-grid">
            {canPickRequester ? (
              <label className="fin-modal-field full">
                <span className="fin-modal-label">Requested by</span>
                <select
                  className="fin-modal-select"
                  value={requesterUserId}
                  onChange={e => setRequesterUserId(e.target.value)}
                >
                  {requesterOptions.map(u => (
                    <option key={u.uid} value={u.uid}>
                      {u.displayName}
                    </option>
                  ))}
                </select>
                <p className="po-spend-hint">
                  Approval and the request trail use this person — not who is signed in to enter the form.
                </p>
              </label>
            ) : (
              <div className="fin-modal-field full">
                <span className="fin-modal-label">Requested by</span>
                <p className="po-spend-hint">{effectiveRequesterName}</p>
              </div>
            )}
            <label className="fin-modal-field">
              <span className="fin-modal-label">Vendor (optional)</span>
              <input className="fin-modal-input" value={vendorName} onChange={e => setVendorName(e.target.value)} />
              <p className="po-spend-hint">Lines can name their own store. Each store becomes its own purchase order.</p>
            </label>
            <SpendClassificationFields
              spendClass={spendClass}
              onSpendClassChange={next => {
                setSpendClass(next);
                if (next !== 'job') {
                  setJobPicker('');
                  setPlBucket('');
                }
                if (next !== 'overhead') {
                  setOverheadGroup('');
                  setForecastLineId('');
                }
              }}
              overheadGroup={overheadGroup}
              onOverheadGroupChange={setOverheadGroup}
              jobPicker={jobPicker}
              onJobPickerChange={setJobPicker}
              plBucket={plBucket}
              onPlBucketChange={setPlBucket}
              dealOptions={dealOptions}
              forecastLineField={canViewBudgets ? (
                <ForecastLineSelect
                  value={forecastLineId}
                  lines={forecastLines}
                  onChange={setForecastLineId}
                />
              ) : null}
              opexCard={
                canViewBudgets && opexSummary ? (
                  <div className="fin-modal-field full">
                    <BudgetCapCard
                      title={`${opexSummary.periodLabel} OpEx remaining`}
                      over={overBudget}
                      note={forecastCoverageNote(forecastLineId, forecastLines)}
                      lines={[
                        { label: 'Budgeted', value: budgetedCardValue(opexSummary) },
                        { label: 'Reserved', value: money(opexSummary.reservedUsd) },
                        { label: 'Committed', value: money(opexSummary.committedUsd) },
                        { label: 'Remaining', value: opexSummary.remainingUsd == null ? 'Not set' : money(opexSummary.remainingUsd) },
                      ]}
                    />
                  </div>
                ) : null
              }
              jobCard={
                canViewBudgets && jobSummary ? (
                  <div className="fin-modal-field full">
                    <BudgetCapCard
                      title={jobBudgetCardTitle(plBucket)}
                      over={overBudget}
                      lines={[
                        { label: 'Budget', value: money(jobSummary.budgetUsd) },
                        { label: 'QB actual', value: money(jobSummary.actualUsd) },
                        { label: 'Reserved', value: money(jobSummary.reservedUsd) },
                        { label: 'Committed', value: money(jobSummary.committedUsd) },
                        { label: 'Remaining', value: money(jobSummary.remainingUsd) },
                      ]}
                    />
                  </div>
                ) : null
              }
            />
            <div className="fin-modal-field full">
              <PurchaseLineItemsEditor
                drafts={lines}
                onChange={setLines}
                salesTaxPct={policy.salesTaxPct}
                headerVendor={vendorName}
              />
            </div>
            <label className="fin-modal-field full">
              <span className="fin-modal-label">Justification</span>
              <textarea className="fin-modal-textarea" rows={4} value={justification} onChange={e => setJustification(e.target.value)} placeholder="Why this purchase, and why now?" />
            </label>
            <div className="fin-modal-field full">
              <PurchaseLinksEditor drafts={links} onChange={setLinks} />
            </div>
            <div className="fin-modal-field full">
              <span className="fin-modal-label">Screenshots / quote docs (optional)</span>
              <PendingPurchaseFiles
                files={files}
                onChange={setFiles}
                addLabel={files.length ? 'Add another screenshot' : 'Add screenshots or PDFs'}
              />
            </div>
          </div>
          <ApprovalMap
            steps={parsedAmount > 0 ? resolved.steps : []}
            origin={{ userName: effectiveRequesterName }}
            bandLabel={resolved.bandLabel}
            emptyMessage={resolved.error || 'Add line amounts to see the approval path.'}
            overBudget={overBudget}
            canOverride={canOverride}
            allowSubmitOverBudget={overBudget && !budgetBlocksSubmit && !canOverride}
          />
          {resolved.selfAuthorized && parsedAmount > 0 && (
            <div className="po-approve">
              <p className="po-self-auth">
                No one sits above you, so you will issue this PO now. It will be paid from the Sequence
                Project Card.
              </p>
              <label className="fin-modal-field">
                <span className="fin-modal-label">Payment method</span>
                <select className="fin-modal-select" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                  {issueMethods.map(m => (
                    <option key={m.id} value={m.label}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="fin-modal-field">
                <span className="fin-modal-label">Payment note (optional)</span>
                <input className="fin-modal-input" value={paymentNote} onChange={e => setPaymentNote(e.target.value)} />
              </label>
              {canViewBudgets ? <ProjectCardBalanceCheck amount={parsedAmount} /> : null}
            </div>
          )}
          {error && <p className="po-error">{error}</p>}
        </div>
        <div className="fin-modal-footer">
          <button type="button" className="fin-secondary-btn" onClick={discardDraftAndClose}>
            Discard draft
          </button>
          <div className="fin-modal-footer-right">
            <button type="button" className="fin-secondary-btn" onClick={closeAndPersistDraft}>
              Close & keep draft
            </button>
            <button
              type="button"
              className="fin-primary-btn"
              disabled={busy || budgetBlocksSubmit}
              onClick={() => void submit()}
            >
              {busy ? 'Submitting…' : resolved.selfAuthorized ? 'Submit & issue PO' : 'Submit requisition'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequisitionDetail({
  row,
  actorId,
  actorName,
  accessArgs,
  canOverride,
  canViewBudgets,
  crmUsers,
  policy,
  resolverUsers,
  dealOptions,
  timberProjects,
  enhancedDeals,
  rows,
  opexBudgets,
  busy,
  error,
  onClose,
  onBusy,
  onError,
}: {
  row: PurchaseRequisition;
  actorId: string;
  actorName: string;
  accessArgs: PurchasingActorAccess;
  canOverride: boolean;
  canViewBudgets: boolean;
  crmUsers: Array<{ uid: string; displayName: string; isActive?: boolean; role?: string }>;
  policy: PurchasingPolicy;
  resolverUsers: ReturnType<typeof crmUsersToResolverUsers>;
  dealOptions: QBDealOption[];
  timberProjects: TimberProject[];
  enhancedDeals: EnhancedDeal[];
  rows: PurchaseRequisition[];
  opexBudgets: OpexBudgets;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onBusy: (v: boolean) => void;
  onError: (v: string | null) => void;
}) {
  const pending = row.approvalSteps.find(s => s.status === 'pending');
  const canReview = canReviewPurchaseRequisition(accessArgs, row, actorId);
  const canActPending = pending && canActOnPendingPurchaseStep(resolverUsers, actorId, pending, accessArgs);
  const approvalSubstitute = purchaseApprovalUsesSubstitutedActor(pending, actorId, resolverUsers, accessArgs);
  const agentAlreadySubmitted = agentDraftAlreadyInApproval(row);
  const awaitingApproval =
    row.status === 'pending_approval' || row.status === 'in_review' || agentAlreadySubmitted;
  const isRequester = row.requesterId === actorId;
  const requesterLabel = purchaseRequesterDisplayName(row, crmUsers);
  const requestedByHeader = purchaseRequestedByHeaderLabel(row, crmUsers);
  const purchaserLabel = purchasePurchaserDisplayName(row);
  const canReassignRequester = canReassignPurchaseRequester(accessArgs);
  const requesterOptions = useMemo(
    () =>
      crmUsers
        .filter(u => u.isActive !== false && u.role !== 'supplier' && u.role !== 'client')
        .slice()
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers],
  );
  const [requesterEditId, setRequesterEditId] = useState(row.requesterId);
  const requesterEditDirty = requesterEditId !== row.requesterId;
  const [noteError, setNoteError] = useState<string | null>(null);
  const noteFieldRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    setRequesterEditId(row.requesterId);
  }, [row.id, row.requesterId]);
  useEffect(() => {
    setNoteError(null);
  }, [row.id]);
  const canCancel = canCancelPurchaseRequisition(accessArgs, row, actorId);
  const isCurrent =
    (awaitingApproval && !!canActPending) || (canOverride && !!row.hold);
  const canDeclineAsApprover = awaitingApproval && !!canActPending && !isRequester;
  const requesterAccountName = purchaseRequesterDirectoryName(row.requesterId, crmUsers, row.requesterName);
  const qbExpenses = useQbExpensesProjectPL();
  const resolvedQbBill = useMemo(
    () => resolvePurchaseQbBillFields(row, qbExpenses),
    [row, qbExpenses],
  );
  const effectiveQbBillStatus = resolvedQbBill?.qbBillStatus ?? row.qbBillStatus;
  const effectiveQbBillId = resolvedQbBill?.qbBillId ?? row.qbBillId;
  const effectiveQbBillUrl = resolvedQbBill?.qbBillUrl ?? row.qbBillUrl;
  const effectiveQbBillMessage = resolvedQbBill?.qbBillMessage ?? row.qbBillMessage;
  const agentReturnBlockedByBill =
    !!effectiveQbBillId ||
    effectiveQbBillStatus === 'linked' ||
    row.purchasedVia === 'supplier_bill' ||
    (row.status === 'approved' && isAchPurchasePayment(row.paymentMethod));
  const canReturnAgentPurchase = canReturnAgentCreatedPurchase({
    createdViaAgent: row.createdViaAgent,
    alreadyPurchased: row.alreadyPurchased,
    status: row.status,
    requesterId: row.requesterId,
    actorId,
    actorIsPendingApprover: !!canActPending,
    actorCanReview: canReview,
    blockedByBill: agentReturnBlockedByBill,
  });
  // CRM #73: issued CRM POs (not only agent) can be returned so the requester
  // can change store / re-source. Withdraws per-store PO numbers on return.
  const canReturnIssuedPurchase = canReturnIssuedPurchaseToRequester({
    status: row.status,
    requesterId: row.requesterId,
    actorId,
    actorCanReview: canReview,
    blockedByBill: agentReturnBlockedByBill,
  });
  const showStandardReturn = isCurrent && awaitingApproval;
  const supplierPoPurchased = (row.orderPurchases?.length || 0) > 0;
  const showReturn = showStandardReturn || canReturnAgentPurchase || (canReturnIssuedPurchase && !supplierPoPurchased);
  const returnNoteVisible =
    showReturn &&
    !(isCurrent && row.status !== 'approved' && row.status !== 'cancelled' && row.status !== 'purchased');
  const returnedStep = [...row.approvalSteps].reverse().find(step => step.status === 'returned');
  const canWithdrawCancel =
    canCancel &&
    (!canDeclineAsApprover ||
      row.status === 'approved' ||
      row.status === 'purchased' ||
      !!row.poNumber);
  const isFinal = !!pending && row.approvalSteps.filter(s => s.status === 'pending').length === 1;
  const canResubmit = isRequester && (row.status === 'returned' || row.status === 'rejected');
  const canSubmitDraft =
    row.status === 'draft' && !agentAlreadySubmitted && (isRequester || canOverride || canReview);
  const canReviseAmount = canReviseIssuedPurchaseAmount(accessArgs, row, actorId);
  const canEditPurchasedAmounts = canEditPurchasedPurchaseAmounts(accessArgs, row, actorId);
  const canPurchase = canMarkPurchasePurchased(accessArgs, row, actorId);
  const { canManage: canManagePayables } = useBillingPayablesAccess();
  const followsBill = purchaseFollowsSupplierBill({
    id: row.paymentMethodId,
    label: row.paymentMethod,
  });
  const [linkedSupplierBill, setLinkedSupplierBill] = useState<SupplierBill | null>(null);
  const [markPaidBusy, setMarkPaidBusy] = useState(false);
  const [retryQbBusy, setRetryQbBusy] = useState(false);
  useEffect(() => {
    if (!followsBill || row.status === 'purchased' || row.status === 'cancelled') {
      setLinkedSupplierBill(null);
      return undefined;
    }
    let cancelled = false;
    void findSupplierBillForPurchaseOrder(row.id).then((bill) => {
      if (!cancelled) setLinkedSupplierBill(bill);
    }).catch(() => {
      if (!cancelled) setLinkedSupplierBill(null);
    });
    return () => { cancelled = true; };
  }, [followsBill, row.id, row.status, row.updatedAt]);
  const billPaymentGuide = billPaymentPurchaseUiGuide({
    paymentMethod: { id: row.paymentMethodId, label: row.paymentMethod },
    designerInvoiceId: row.designerInvoiceId,
    dealId: row.dealId || row.costObject.dealId,
    supplierBillId: linkedSupplierBill?.id || null,
    timberProjects,
    deals: enhancedDeals,
  });
  /** Bill Payment POs normally close in B&P; when that path is unavailable, allow Mark purchased. */
  const billPaymentHasClosePath = billPaymentPurchaseHasUiClosePath(billPaymentGuide);
  const canManualCloseBillPayment = followsBill && !billPaymentHasClosePath;
  const linkedBillHasQb = Boolean(
    linkedSupplierBill?.qbBillId
    || effectiveQbBillId
    || linkedSupplierBill?.status === 'entered_in_qb'
    || linkedSupplierBill?.status === 'ready_to_pay',
  );
  const canMarkBillPaidFromPo = Boolean(
    canManagePayables
    && followsBill
    && row.status === 'approved'
    && linkedSupplierBill
    && linkedBillHasQb
    && canTransitionSupplierBill(linkedSupplierBill.status, 'paid')
    && (canManualCloseBillPayment || Boolean(row.designerInvoiceId)),
  );
  const canRetryQbBillFromPo = Boolean(
    canManagePayables
    && followsBill
    && linkedSupplierBill
    && linkedSupplierBill.status === 'ready_to_pay'
    && !String(linkedSupplierBill.qbBillId || effectiveQbBillId || '').trim(),
  );
  const canDelete = canDeletePurchaseRequisition(accessArgs, row, actorId);
  const canHold = canReview && (row.status === 'pending_approval' || row.status === 'in_review' || row.status === 'approved');
  const canManageSupporting = canManagePurchaseSupportingAttachments(accessArgs, row, actorId);
  const canRemoveReceipt = canRemovePurchaseReceipt(accessArgs, row, actorId);
  const docLabel = purchaseOrderNumbers(row).join(', ') || row.prNumber;

  const [note, setNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(
    () => row.paymentMethod || defaultSelfIssuePaymentMethod(policy.paymentMethods),
  );
  const [paymentNote, setPaymentNote] = useState(row.paymentMethodNote || '');
  const issueMethods = withProjectCardPaymentMethod(policy.paymentMethods);
  const [copied, setCopied] = useState(false);
  const [resubmit, setResubmit] = useState(canResubmit);
  const [reviseAmount, setReviseAmount] = useState(false);
  const [editPurchased, setEditPurchased] = useState(false);
  const [editPurchasedAmountInput, setEditPurchasedAmountInput] = useState(() => row.amount.toFixed(2));
  const [editPurchasedReceiptInput, setEditPurchasedReceiptInput] = useState(() =>
    row.purchasedAmount != null ? row.purchasedAmount.toFixed(2) : row.amount.toFixed(2),
  );
  const [editPurchasedNote, setEditPurchasedNote] = useState('');
  const [vendorName, setVendorName] = useState(row.vendorName || '');
  const [justification, setJustification] = useState(row.justification);
  const [lines, setLines] = useState(() => lineItemsToDrafts(row.lineItems));
  const [links, setLinks] = useState(() =>
    linksToDrafts(additionalPurchaseLinks(row.links, row.lineItems)),
  );
  const [spendClass, setSpendClass] = useState<PurchaseSpendClass>(() => resolveSpendClass(row.costObject));
  const [overheadGroup, setOverheadGroup] = useState<PurchaseOverheadGroup | ''>(
    () => row.costObject.overheadGroup || '',
  );
  const [forecastLineId, setForecastLineId] = useState(
    () => row.overheadExpenseId || row.costObject.overheadExpenseId || '',
  );
  const [forecastLineBusy, setForecastLineBusy] = useState(false);
  const forecastLines = useOverheadForecastLines(canViewBudgets && spendClass === 'overhead');
  const [plBucket, setPlBucket] = useState<PurchaseJobPlBucket | ''>(() => row.costObject.plBucket || '');
  const [jobPicker, setJobPicker] = useState(() => jobPickerValueFromCostObject(row.costObject));
  const [internalHold, setInternalHold] = useState<PurchaseHoldInternal | null>(null);
  const [holdReasonId, setHoldReasonId] = useState(policy.holdReasons[0]?.id || 'waiting_cash');
  const [holdNote, setHoldNote] = useState('');
  const selectedHoldReason = policy.holdReasons.find(r => r.id === holdReasonId) || policy.holdReasons[0];
  const [holdVisible, setHoldVisible] = useState(selectedHoldReason?.visibleToRequesterDefault ?? false);
  const [holdFormOpen, setHoldFormOpen] = useState(false);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const [attachmentViewer, setAttachmentViewer] = useState<{
    url: string;
    title: string;
    kind: 'pdf' | 'image';
    documentId: string;
    mimeType?: string;
    initialAnnotationId?: string;
    initialPageNumber?: number;
  } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmPurchase, setConfirmPurchase] = useState(!!row.purchaseVariance && !row.purchaseVariance.poNumber);
  const [purchaseTargetPo, setPurchaseTargetPo] = useState<string | null>(
    () => row.purchaseVariance?.poNumber || null,
  );
  const [actualAmountInput, setActualAmountInput] = useState(() =>
    row.purchaseVariance
      ? row.purchaseVariance.proposedAmount.toFixed(2)
      : row.amount.toFixed(2),
  );

  useEffect(() => {
    setVendorName(row.vendorName || '');
    setJustification(row.justification);
    setLines(lineItemsToDrafts(row.lineItems));
    setLinks(linksToDrafts(additionalPurchaseLinks(row.links, row.lineItems)));
    setSpendClass(resolveSpendClass(row.costObject));
    setOverheadGroup(row.costObject.overheadGroup || '');
    setForecastLineId(row.overheadExpenseId || row.costObject.overheadExpenseId || '');
    setPlBucket(row.costObject.plBucket || '');
    setJobPicker(jobPickerValueFromCostObject(row.costObject));
    setResubmit(row.status === 'returned' || row.status === 'rejected');
    setReviseAmount(false);
    setEditPurchased(false);
    setEditPurchasedAmountInput(row.amount.toFixed(2));
    setEditPurchasedReceiptInput(
      row.purchasedAmount != null ? row.purchasedAmount.toFixed(2) : row.amount.toFixed(2),
    );
    setEditPurchasedNote('');
    setConfirmPurchase(!!row.purchaseVariance && !row.purchaseVariance.poNumber);
    setPurchaseTargetPo(row.purchaseVariance?.poNumber || null);
    setHoldFormOpen(false);
    setReceiptFiles([]);
    setActualAmountInput(
      row.purchaseVariance
        ? row.purchaseVariance.proposedAmount.toFixed(2)
        : row.amount.toFixed(2),
    );
    onError(null);
  }, [row.id, row.status, row.purchaseVariance?.proposedAmount, row.purchaseVariance?.reviewerId, row.purchaseVariance?.poNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canReview) {
      setInternalHold(null);
      return;
    }
    return subscribePurchaseInternalReview(row.id, setInternalHold);
  }, [row.id, canReview]);

  useEffect(() => {
    const reason = policy.holdReasons.find(r => r.id === holdReasonId);
    if (reason) setHoldVisible(reason.visibleToRequesterDefault);
  }, [holdReasonId, policy.holdReasons]);

  const actualAuthority = resolvePurchaseActualAuthority({
    actorId,
    users: resolverUsers,
    fallbackApproverUserId: policy.fallbackApproverUserId,
  });
  const parsedActual = parsePurchaseUsdInput(actualAmountInput);
  const supplierOrders = issuedSupplierPurchaseOrders(row);
  const multiSupplierPos = supplierOrders.length > 1;
  const purchaseTarget = supplierOrders.find(order => order.poNumber === purchaseTargetPo);
  const purchaseTargetAmount = purchaseTarget ? purchaseTarget.amount : row.amount;
  const actualWithinTol = parsedActual != null && purchaseActualWithinTolerance(purchaseTargetAmount, parsedActual);
  const canSelfConfirmActual = canSelfConfirmPurchaseActual({
    canOverride,
    actorId,
    authorityUserId: actualAuthority?.userId,
    varianceReviewerId: row.purchaseVariance?.reviewerId,
  });
  const showPurchaseForm =
    canPurchase
    && (!followsBill || canManualCloseBillPayment)
    && (multiSupplierPos
      ? !!purchaseTarget
      : (confirmPurchase || (!!row.purchaseVariance && !row.purchaseVariance.poNumber)));
  const existingReceipts = purchaseReceipts(row.attachments);
  const supportingFiles = purchaseSupportingFiles(row.attachments);
  const receiptLinks = (row.links || []).filter(link => link.url?.trim());
  // Screenshots under "Screenshots / quote docs" and receipt links count as proof (CRM #53).
  const receiptReady =
    hasPurchaseReceiptProof(row.attachments, receiptLinks) || receiptFiles.length > 0;
  const hasExistingReceiptProof =
    existingReceipts.length > 0 || supportingFiles.length > 0 || receiptLinks.length > 0;
  const canAddReceipt = canUploadPurchaseReceipt(accessArgs, row, actorId);

  const openAttachmentViewer = async (
    file: PurchaseAttachment,
    href?: string,
    opts?: { annotationId?: string; pageNumber?: number },
  ) => {
    const kind = purchaseReceiptPreviewKind(file);
    if (!kind) {
      if (href) window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    let url = href || file.url || '';
    if (!url && file.storagePath) {
      try {
        url = await getDownloadURL(storageRef(storage, file.storagePath));
      } catch {
        return;
      }
    }
    if (!url) return;
    setAttachmentViewer({
      url,
      title: file.name,
      kind,
      documentId: buildPurchaseAttachmentDocumentId(row.id, file.storagePath || file.name),
      mimeType: file.contentType,
      initialAnnotationId: opts?.annotationId,
      initialPageNumber: opts?.pageNumber,
    });
  };

  const closeAttachmentViewer = () => {
    setAttachmentViewer(null);
    const next = new URLSearchParams(searchParams);
    next.delete('file');
    next.delete('comment');
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const fileParam = searchParams.get('file')?.trim();
    if (!fileParam || attachmentViewer) return;
    const match = row.attachments.find(
      file => buildPurchaseAttachmentDocumentId(row.id, file.storagePath || file.name) === fileParam,
    );
    if (!match) return;
    const pageRaw = searchParams.get('page');
    const pageNumber = pageRaw != null && pageRaw !== '' ? Number(pageRaw) : undefined;
    void openAttachmentViewer(match, undefined, {
      annotationId: searchParams.get('comment') || undefined,
      pageNumber: pageNumber != null && !Number.isNaN(pageNumber) ? pageNumber : undefined,
    });
    // Only react to deep-link params / row identity — not attachmentViewer itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, searchParams]);

  const lineItems = draftsToLineItems(lines);
  const parsedAmount = requisitionTotal(lineItems, policy.salesTaxPct);
  const costObject = costObjectFromSpendFields(
    spendClass,
    overheadGroup,
    jobPicker,
    dealOptions,
    timberProjects,
    plBucket,
    forecastLineId,
  );
  const budgetDealId = proposalDealIdForBudget(
    costObject.dealId || row.costObject.dealId || row.dealId,
    timberProjects,
  );
  const timber = budgetDealId ? timberForDeal(timberProjects, budgetDealId) : null;
  const dealId = budgetDealId;
  const timberProjectId = timber?.id || (dealId ? timberForDeal(timberProjects, dealId)?.id : undefined);
  const jobBudget = useJobPurchaseBudget(budgetDealId, plBucket || row.costObject.plBucket);
  const opexGroup = (overheadGroup || row.costObject.overheadGroup) as PurchaseOverheadGroup | undefined;
  const opexEnvelope = opexGroup ? groupBudgetEnvelope(opexBudgets, opexGroup) : null;
  const opexBudgetArgs =
    opexGroup &&
    opexEnvelope &&
    (spendClass === 'overhead' || resolveSpendClass(row.costObject) === 'overhead')
      ? {
          rows,
          group: opexGroup,
          budgetedUsd: opexEnvelope.amountUsd,
          interval: opexEnvelope.interval,
        }
      : null;
  const opexSummary = opexBudgetArgs ? summarizeOpexGroupMonth(opexBudgetArgs) : null;
  const opexRemainingForCap = opexBudgetArgs
    ? summarizeOpexGroupMonth({ ...opexBudgetArgs, excludeId: row.id }).remainingUsd
    : null;
  const jobSummaryDealId = budgetDealId;
  const jobSummaryBucket = plBucket || row.costObject.plBucket;
  const jobSummary =
    jobSummaryDealId && jobSummaryBucket
      ? summarizeJobNode({
          rows,
          dealId: jobSummaryDealId,
          plBucket: jobSummaryBucket,
          budgetUsd: jobBudget.budgetUsd,
          actualUsd: jobBudget.actualUsd,
        })
      : null;
  const jobRemainingForCap =
    jobSummaryDealId && jobSummaryBucket
      ? summarizeJobNode({
          rows,
          dealId: jobSummaryDealId,
          plBucket: jobSummaryBucket,
          budgetUsd: jobBudget.budgetUsd,
          actualUsd: jobBudget.actualUsd,
          excludeId: row.id,
        }).remainingUsd
      : null;
  const amountForCap = parsedAmount || row.amount;
  const paysForecastLine = spendClass === 'overhead' && !!forecastLineId.trim();
  const overBudget = purchaseIsOverBudget({
    paysForecastLine,
    ...(opexSummary
      ? { opexRemainingUsd: opexRemainingForCap }
      : jobSummary
        ? { jobRemainingUsd: jobRemainingForCap, jobBudgetReady: jobBudget.loaded }
        : {}),
    amountUsd: amountForCap,
  });
  const saveForecastLine = async (nextId: string) => {
    const previous = forecastLineId;
    setForecastLineId(nextId);
    setForecastLineBusy(true);
    onError(null);
    try {
      await assignPurchaseForecastLine({
        requisition: row,
        overheadExpenseId: nextId.trim() || null,
      });
    } catch (err) {
      setForecastLineId(previous);
      onError(err instanceof Error ? err.message : 'Could not update the forecast line.');
    } finally {
      setForecastLineBusy(false);
    }
  };
  const resolved = resolveApprovalChain({
    requesterId: row.requesterId,
    amount: parsedAmount || row.amount,
    costType: costObject.type,
    spendClass,
    projectManagerUserId: timber?.projectManager,
    users: resolverUsers,
    policy,
  });
  const budgetBlocksIssue = overBudget && !canOverride && !!resolved.selfAuthorized;
  const approvedAmountBaseline = approvedPurchaseAmountBaseline(row);
  const amountEditKind = issuedPurchaseAmountEditKind(approvedAmountBaseline, parsedAmount || row.amount);
  // CRM #75: vendor/store-only edits must enable Update even when the total is unchanged.
  const reviseStoresChanged = vendorGroupKeysChanged(
    row.lineItems,
    row.vendorName,
    lineItems,
    vendorName,
  );
  const canSubmitRevise = canSubmitIssuedPurchaseRevise(amountEditKind, reviseStoresChanged);
  const showAmountEditor = (canResubmit && resubmit) || (canReviseAmount && reviseAmount);
  const previewNewGates = (canResubmit && resubmit) || (reviseAmount && amountEditKind === 'needs_approval');
  const showProjectCardCheck =
    canViewBudgets &&
    !followsBill &&
    row.status !== 'cancelled' &&
    row.status !== 'purchased' &&
    (isSelfAuthorizedPurchase(row) || resolved.selfAuthorized);

  const run = async (fn: () => Promise<void>) => {
    onBusy(true);
    onError(null);
    try {
      await fn();
    } catch (e) {
      // Prefer the thrown message (often already formatted for Mark purchased /
      // receipt upload). Fall back to a diagnosable Firebase code when present.
      const message = e instanceof Error ? e.message.trim() : '';
      const code =
        e && typeof e === 'object' && 'code' in e
          ? String((e as { code?: unknown }).code || '').trim()
          : '';
      if (message) {
        onError(code && !message.includes(code) ? `${message} (${code})` : message);
      } else if (code) {
        onError(`Something went wrong (${code}).`);
      } else {
        onError('Something went wrong.');
      }
    } finally {
      onBusy(false);
    }
  };

  const poNumbers = purchaseOrderNumbers(row);
  const vendorPoLabel = purchaseVendorPoLabel(row.lineItems, row.vendorName, row.poNumber);
  const copyPo = async () => {
    if (!poNumbers.length) return;
    await navigator.clipboard.writeText(poNumbers.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div className="fin-modal po-modal" onClick={e => e.stopPropagation()}>
        <div className="fin-modal-header">
          <div>
            <h3>{row.prNumber}</h3>
            <p className="po-requester-meta">
              Requested by{' '}
              {canReassignRequester ? (
                <select
                  className="fin-modal-select po-requester-select"
                  value={requesterEditId}
                  onChange={e => setRequesterEditId(e.target.value)}
                >
                  {requesterOptions.map(u => (
                    <option key={u.uid} value={u.uid}>
                      {purchaseRequesterOptionLabel(u, row)}
                    </option>
                  ))}
                </select>
              ) : (
                <strong>{requestedByHeader}</strong>
              )}
            </p>
          </div>
          <div className="po-header-actions">
            <button
              type="button"
              className="po-print-btn"
              onClick={() => printPurchaseChecklist(poNumbers.join(', ') || row.prNumber)}
            >
              <Printer size={14} />
              Print
            </button>
            <button type="button" className="fin-modal-close" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="fin-modal-body">
          {poNumbers.length > 0 && (
            <div className="po-issued">
              <div className="po-issued-kicker">
                {supplierOrders.length > 1
                  ? `Purchase orders · ${supplierOrders.filter(order => row.orderPurchases?.some(done => done.poNumber === order.poNumber)).length} of ${supplierOrders.length} purchased`
                  : 'Purchase order'}
              </div>
              {supplierOrders.length > 1 ? (
                <table className="po-issued-table">
                  <thead>
                    <tr>
                      <th scope="col">PO</th>
                      <th scope="col">Supplier</th>
                      <th scope="col" className="num">Amount</th>
                      <th scope="col" className="action"><span className="sr-only">Action</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierOrders.map(order => {
                      const done = row.orderPurchases?.find(entry => entry.poNumber === order.poNumber);
                      const waiting = row.purchaseVariance?.poNumber === order.poNumber;
                      const canMarkThis =
                        canPurchase
                        && (!followsBill || canManualCloseBillPayment)
                        && !done;
                      return (
                        <tr key={order.poNumber}>
                          <td className="po">{order.poNumber}</td>
                          <td>{order.vendorName || '—'}</td>
                          <td className="num">{money(order.amount)}</td>
                          <td className="action">
                            {done ? (
                              <span className="po-issued-group-status">
                                Purchased {money(done.purchasedAmount)}
                                {done.purchasedAt ? ` · ${done.purchasedAt.toLocaleDateString()}` : ''}
                              </span>
                            ) : waiting ? (
                              <span className="po-issued-group-status">
                                Waiting on {row.purchaseVariance?.reviewerName || 'a supervisor'}
                                {' '}
                                <button
                                  type="button"
                                  className="fin-secondary-btn"
                                  disabled={busy}
                                  onClick={() => {
                                    setPurchaseTargetPo(order.poNumber);
                                    setActualAmountInput((row.purchaseVariance?.proposedAmount ?? order.amount).toFixed(2));
                                    setReceiptFiles([]);
                                  }}
                                >
                                  Review receipt
                                </button>
                              </span>
                            ) : canMarkThis ? (
                              <button
                                type="button"
                                className="fin-secondary-btn"
                                disabled={busy}
                                onClick={() => {
                                  onError(null);
                                  setPurchaseTargetPo(order.poNumber);
                                  setActualAmountInput(order.amount.toFixed(2));
                                  setReceiptFiles([]);
                                }}
                              >
                                Mark purchased
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="po-issued-number">{poNumbers[0]}</div>
              )}
              <div className="po-issued-pay">
                Pay by {row.paymentMethod}
                {row.paymentMethodNote ? ` — ${row.paymentMethodNote}` : ''}
              </div>
              {billPaymentGuide && row.status !== 'purchased' && row.status !== 'cancelled' && (
                <p className="po-issued-bill-note">{billPaymentGuide.stepNote}</p>
              )}
              <div className="po-issued-actions">
                {billPaymentGuide?.billingPayablesHref ? (
                  <Link className="fin-secondary-btn" to={billPaymentGuide.billingPayablesHref}>
                    <ExternalLink size={14} />
                    Open in Billing & Payables
                  </Link>
                ) : null}
                {billPaymentGuide?.secondaryLink ? (
                  <Link className="fin-secondary-btn" to={billPaymentGuide.secondaryLink.to}>
                    <ExternalLink size={14} />
                    {billPaymentGuide.secondaryLink.label}
                  </Link>
                ) : null}
                <button type="button" className="fin-secondary-btn" onClick={() => void copyPo()}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied' : poNumbers.length > 1 ? 'Copy PO numbers' : 'Copy PO number'}
                </button>
              </div>
              <PurchaseQbBillIndicator
                status={effectiveQbBillStatus}
                url={effectiveQbBillUrl}
                message={effectiveQbBillMessage}
                vendorName={row.vendorName}
                amount={row.amount}
                invoiceNumber={row.designerInvoiceNumber}
              />
            </div>
          )}
          {showProjectCardCheck && row.poNumber ? <ProjectCardBalanceCheck amount={row.amount} /> : null}

          {row.status === 'purchased' && (
            <div className="po-purchased">
              <div className="po-issued-kicker">Purchased</div>
              <p>
                {row.alreadyPurchased
                  ? 'Recorded as already purchased'
                  : row.purchasedVia === 'supplier_bill'
                    ? 'Closed when the final bill was marked paid in Billing & Payables'
                    : 'Marked purchased'}
                {purchaserLabel ? ` by ${purchaserLabel}` : ''}
                {row.purchasedAt ? ` on ${row.purchasedAt.toLocaleDateString()}` : ''}.
              </p>
              {row.purchasedAmount != null ? (
                <p>
                  Receipt {money(row.purchasedAmount)}
                  {Math.abs(row.purchasedAmount - row.amount) > 0.005 ? ` · PO ${money(row.amount)}` : ''}
                </p>
              ) : null}
              {canEditPurchasedAmounts && !editPurchased ? (
                <button
                  type="button"
                  className="fin-secondary-btn"
                  disabled={busy}
                  onClick={() => {
                    onError(null);
                    setEditPurchasedAmountInput(row.amount.toFixed(2));
                    setEditPurchasedReceiptInput(
                      row.purchasedAmount != null ? row.purchasedAmount.toFixed(2) : row.amount.toFixed(2),
                    );
                    setEditPurchasedNote('');
                    setEditPurchased(true);
                  }}
                >
                  Edit amounts
                </button>
              ) : null}
              {editPurchased && canEditPurchasedAmounts ? (
                <div className="po-purchased-edit">
                  <label className="po-field">
                    <span>PO amount</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={editPurchasedAmountInput}
                      onChange={e => setEditPurchasedAmountInput(e.target.value)}
                      disabled={busy}
                    />
                  </label>
                  <label className="po-field">
                    <span>Purchased / receipt amount</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={editPurchasedReceiptInput}
                      onChange={e => setEditPurchasedReceiptInput(e.target.value)}
                      disabled={busy}
                    />
                  </label>
                  <label className="po-field">
                    <span>Note (required)</span>
                    <textarea
                      value={editPurchasedNote}
                      onChange={e => setEditPurchasedNote(e.target.value)}
                      rows={2}
                      disabled={busy}
                      placeholder="Why are these amounts changing?"
                    />
                  </label>
                  {(() => {
                    const nextAmt = parsePurchaseUsdInput(editPurchasedAmountInput);
                    const nextPurchased = parsePurchaseUsdInput(editPurchasedReceiptInput);
                    const baseline = approvedPurchaseAmountBaseline(row);
                    if (nextAmt == null || nextPurchased == null) {
                      return <p className="po-actual-hint">Enter both amounts.</p>;
                    }
                    const kind = purchasedAmountEditKind({
                      approvedAmount: row.approvedAmount,
                      amount: row.amount,
                      nextAmount: nextAmt,
                      nextPurchasedAmount: nextPurchased,
                    });
                    return (
                      <p className={`po-actual-hint${kind === 'needs_approval' ? ' po-actual-hint--over' : ''}`}>
                        {formatPurchasedAmountEditHint(baseline, nextAmt, nextPurchased)}
                      </p>
                    );
                  })()}
                  <div className="po-purchased-edit-actions">
                    <button
                      type="button"
                      className="fin-secondary-btn"
                      disabled={busy}
                      onClick={() => {
                        setEditPurchased(false);
                        setEditPurchasedNote('');
                        onError(null);
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="fin-primary-btn"
                      disabled={busy || !editPurchasedNote.trim()}
                      onClick={() =>
                        void run(async () => {
                          const nextAmt = parsePurchaseUsdInput(editPurchasedAmountInput);
                          const nextPurchased = parsePurchaseUsdInput(editPurchasedReceiptInput);
                          if (nextAmt == null || nextPurchased == null) {
                            throw new Error('Enter valid amounts.');
                          }
                          if (!editPurchasedNote.trim()) throw new Error('Add a note explaining the change.');
                          const kind = purchasedAmountEditKind({
                            approvedAmount: row.approvedAmount,
                            amount: row.amount,
                            nextAmount: nextAmt,
                            nextPurchasedAmount: nextPurchased,
                          });
                          let authorityConfirm = false;
                          if (kind === 'needs_approval') {
                            if (!canOverride) {
                              throw new Error(
                                'This is more than 10% off the approved amount. Ask finance leadership to confirm, or keep the change within 10%.',
                              );
                            }
                            const ok = window.confirm(
                              'This is more than 10% off the approved amount. Apply with your authority override?',
                            );
                            if (!ok) return;
                            authorityConfirm = true;
                          }
                          await editPurchasedPurchaseAmounts({
                            requisition: row,
                            actorId,
                            actorName,
                            access: accessArgs,
                            note: editPurchasedNote,
                            amount: nextAmt,
                            purchasedAmount: nextPurchased,
                            authorityConfirm,
                          });
                          setEditPurchased(false);
                          setEditPurchasedNote('');
                        })
                      }
                    >
                      Save amounts
                    </button>
                  </div>
                </div>
              ) : null}
              {existingReceipts.length > 0 ? (
                <div className="po-receipts">
                  {existingReceipts.map(file => (
                    <SavedAttachmentRow
                      key={file.storagePath}
                      file={file}
                      emphasize
                      canRemove={canRemoveReceipt}
                      busy={busy}
                      onOpen={(href, attachment) => void openAttachmentViewer(attachment, href)}
                      onRemove={() =>
                        void run(() =>
                          removePurchaseAttachment({
                            requisition: row,
                            storagePath: file.storagePath,
                            actorId,
                            access: accessArgs,
                          }),
                        )
                      }
                    />
                  ))}
                  {canAddReceipt ? (
                    <label className="po-receipt-add">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        className="po-file-hidden"
                        onChange={e => {
                          const next = Array.from(e.target.files || []);
                          e.target.value = '';
                          if (!next.length) return;
                          void run(() => addPurchaseReceipts({ requisition: row, files: next }));
                        }}
                      />
                      Add another receipt
                    </label>
                  ) : null}
                </div>
              ) : canAddReceipt ? (
                <label className="po-receipt-add po-receipt-add--primary">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    className="po-file-hidden"
                    onChange={e => {
                      const next = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (!next.length) return;
                      void run(() => addPurchaseReceipts({ requisition: row, files: next }));
                    }}
                  />
                  Upload receipt
                </label>
              ) : (
                <p>No receipt uploaded yet.</p>
              )}
            </div>
          )}

          {row.status === 'approved' && row.purchaseVariance && (
            <div className="po-variance">
              <div className="po-issued-kicker">Actual review</div>
              <p>
                Receipt {money(row.purchaseVariance.proposedAmount)} is more than 3% off PO {money(row.amount)}.
                Waiting on {row.purchaseVariance.reviewerName || 'your supervisor'}.
              </p>
            </div>
          )}

          {row.status === 'cancelled' && (
            <div className="po-cancelled">
              <div className="po-issued-kicker">Cancelled</div>
              <p>
                Withdrawn
                {row.cancelledByName ? ` by ${row.cancelledByName}` : ''}
                {row.cancelledAt ? ` on ${row.cancelledAt.toLocaleDateString()}` : ''}.
              </p>
            </div>
          )}

          {row.status === 'returned' && (
            <div className="po-hold">
              <div className="po-issued-kicker">Returned for review</div>
              <p>
                {isRequester
                  ? 'An approver sent this back to you for review.'
                  : `Sent back to ${requesterAccountName} for review.`}
                {returnedStep?.note ? ` ${returnedStep.note}` : ''}
              </p>
            </div>
          )}

          {row.status === 'rejected' && (
            <div className="po-cancelled">
              <div className="po-issued-kicker">Declined</div>
              <p>
                An approver declined this requisition. The requester can edit and resubmit, or leave it closed.
              </p>
            </div>
          )}

          {row.hold && (
            <div className="po-hold">
              <div className="po-issued-kicker">On hold</div>
              <p>{canReview && internalHold ? internalHold.reasonLabel : requesterHoldMessage(row.hold)}</p>
              {canReview && internalHold && (
                <div className="po-hold-internal">
                  {internalHold.note ? <p>{internalHold.note}</p> : null}
                  <p className="po-hold-visibility">
                    {internalHold.visibleToRequester
                      ? 'Reason is visible to the requester.'
                      : 'Reason is hidden from the requester.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {showAmountEditor ? (
            <div className="fin-modal-grid">
              <label className="fin-modal-field">
                <span className="fin-modal-label">Vendor</span>
                <input
                  className="fin-modal-input"
                  value={vendorName}
                  onChange={e => {
                    const next = e.target.value;
                    // CRM #75: on Adjust amount, a single-store header edit must also
                    // rewrite stamped line vendors so Update enables and the save persists.
                    if (reviseAmount) {
                      setLines(prev => rewriteUniformVendorLinesOnHeaderChange(prev, vendorName, next));
                    }
                    setVendorName(next);
                  }}
                />
                <p className="po-spend-hint">Lines can name their own store. Each store becomes its own purchase order.</p>
              </label>
              {reviseAmount && (
                <p className={`po-actual-hint${amountEditKind === 'needs_approval' ? ' po-actual-hint--over' : ''}`}>
                  {formatIssuedPurchaseAmountHint(approvedAmountBaseline, parsedAmount || row.amount, {
                    storesChanged: reviseStoresChanged,
                  })}
                  {` Approved total ${money(approvedAmountBaseline)}.`}
                </p>
              )}
              {canResubmit ? (
              <SpendClassificationFields
                spendClass={spendClass}
                onSpendClassChange={next => {
                  setSpendClass(next);
                  if (next !== 'job') {
                    setJobPicker('');
                    setPlBucket('');
                  }
                  if (next !== 'overhead') {
                    setOverheadGroup('');
                    setForecastLineId('');
                  }
                }}
                overheadGroup={overheadGroup}
                onOverheadGroupChange={setOverheadGroup}
                jobPicker={jobPicker}
                onJobPickerChange={setJobPicker}
                plBucket={plBucket}
                onPlBucketChange={setPlBucket}
                dealOptions={dealOptions}
                forecastLineField={canViewBudgets ? (
                  <ForecastLineSelect
                    value={forecastLineId}
                    lines={forecastLines}
                    disabled={forecastLineBusy}
                    onChange={next => { void saveForecastLine(next); }}
                  />
                ) : null}
                opexCard={
                  canViewBudgets && opexSummary ? (
                    <div className="fin-modal-field full">
                      <BudgetCapCard
                        title={`${opexSummary.periodLabel} OpEx remaining`}
                        over={overBudget}
                        note={forecastCoverageNote(forecastLineId, forecastLines)}
                        lines={[
                          { label: 'Budgeted', value: budgetedCardValue(opexSummary) },
                          { label: 'Reserved', value: money(opexSummary.reservedUsd) },
                          { label: 'Committed', value: money(opexSummary.committedUsd) },
                          { label: 'Remaining', value: opexSummary.remainingUsd == null ? 'Not set' : money(opexSummary.remainingUsd) },
                        ]}
                      />
                    </div>
                  ) : null
                }
                jobCard={
                  canViewBudgets && jobSummary ? (
                    <div className="fin-modal-field full">
                      <BudgetCapCard
                        title={jobBudgetCardTitle(plBucket || row.costObject.plBucket)}
                        over={overBudget}
                        lines={[
                          { label: 'Budget', value: money(jobSummary.budgetUsd) },
                          { label: 'QB actual', value: money(jobSummary.actualUsd) },
                          { label: 'Reserved', value: money(jobSummary.reservedUsd) },
                          { label: 'Committed', value: money(jobSummary.committedUsd) },
                          { label: 'Remaining', value: money(jobSummary.remainingUsd) },
                        ]}
                      />
                    </div>
                  ) : null
                }
              />
              ) : canViewBudgets && (opexSummary || jobSummary) ? (
                <>
                  {opexSummary ? (
                    <div className="fin-modal-field full">
                      <BudgetCapCard
                        title={`${opexSummary.periodLabel} OpEx remaining`}
                        over={overBudget}
                        lines={[
                          { label: 'Budgeted', value: budgetedCardValue(opexSummary) },
                          { label: 'Reserved', value: money(opexSummary.reservedUsd) },
                          { label: 'Committed', value: money(opexSummary.committedUsd) },
                          { label: 'Remaining', value: opexSummary.remainingUsd == null ? 'Not set' : money(opexSummary.remainingUsd) },
                        ]}
                      />
                    </div>
                  ) : null}
                  {jobSummary ? (
                    <div className="fin-modal-field full">
                      <BudgetCapCard
                        title={jobBudgetCardTitle(plBucket || row.costObject.plBucket)}
                        over={overBudget}
                        lines={[
                          { label: 'Budget', value: money(jobSummary.budgetUsd) },
                          { label: 'QB actual', value: money(jobSummary.actualUsd) },
                          { label: 'Reserved', value: money(jobSummary.reservedUsd) },
                          { label: 'Committed', value: money(jobSummary.committedUsd) },
                          { label: 'Remaining', value: money(jobSummary.remainingUsd) },
                        ]}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              <div className="fin-modal-field full">
                <PurchaseLineItemsEditor
                drafts={lines}
                onChange={setLines}
                salesTaxPct={policy.salesTaxPct}
                headerVendor={vendorName}
              />
              </div>
              <label className="fin-modal-field full">
                <span className="fin-modal-label">Justification</span>
                <textarea className="fin-modal-textarea" rows={4} value={justification} onChange={e => setJustification(e.target.value)} />
              </label>
              {canResubmit && (
                <>
              <div className="fin-modal-field full">
                <PurchaseLinksEditor drafts={links} onChange={setLinks} />
              </div>
              <div className="fin-modal-field full">
                <SavedPurchaseAttachments
                  files={supportingFiles}
                  canWrite={canManageSupporting}
                  busy={busy}
                  onOpen={(href, attachment) => void openAttachmentViewer(attachment, href)}
                  onAdd={next =>
                    void run(() =>
                      addPurchaseSupportingAttachments({
                        requisition: row,
                        files: next,
                        actorId,
                        access: accessArgs,
                      }),
                    )
                  }
                  onRemove={file =>
                    void run(() =>
                      removePurchaseAttachment({
                        requisition: row,
                        storagePath: file.storagePath,
                        actorId,
                        access: accessArgs,
                      }),
                    )
                  }
                />
              </div>
                </>
              )}
              {(canResubmit || (reviseAmount && amountEditKind === 'needs_approval')) && resolved.selfAuthorized && (
                <>
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">Payment method</span>
                    <select className="fin-modal-select" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                      {issueMethods.map(m => (
                        <option key={m.id} value={m.label}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">Payment note (optional)</span>
                    <input className="fin-modal-input" value={paymentNote} onChange={e => setPaymentNote(e.target.value)} />
                  </label>
                  {showProjectCardCheck && !row.poNumber ? (
                    <ProjectCardBalanceCheck amount={parsedAmount || row.amount} />
                  ) : null}
                </>
              )}
            </div>
          ) : (
            <div className="po-detail-copy">
              {row.rfqSubmissionId && (
                <div className="po-origin-chip">From Other Quotes</div>
              )}
              {row.badge ? (
                <div className="po-origin-chip po-origin-chip--employee-rec">{row.badge}</div>
              ) : (row.origin === 'employee_recommendation' || row.source === 'crew_slack') ? (
                <div className="po-origin-chip po-origin-chip--employee-rec">Employee recommendation</div>
              ) : null}
              <div className="po-detail-title">{row.title}</div>
              <div className="po-detail-meta">
                {money(row.amount)} · {formatCostObjectLabel(row.costObject)}
                {vendorPoLabel ? ` · ${vendorPoLabel}` : ''}
              </div>
              {canViewBudgets && resolveSpendClass(row.costObject) === 'overhead' && (
                <div className="fin-modal-grid">
                  <ForecastLineSelect
                    value={forecastLineId}
                    lines={forecastLines}
                    disabled={forecastLineBusy}
                    onChange={next => { void saveForecastLine(next); }}
                  />
                </div>
              )}
              {canViewBudgets && opexSummary && (
                <BudgetCapCard
                  title={`${opexSummary.periodLabel} OpEx remaining`}
                  over={overBudget}
                  note={forecastCoverageNote(forecastLineId, forecastLines)}
                  lines={[
                    { label: 'Budgeted', value: budgetedCardValue(opexSummary) },
                    { label: 'Reserved', value: money(opexSummary.reservedUsd) },
                    { label: 'Committed', value: money(opexSummary.committedUsd) },
                    { label: 'Remaining', value: opexSummary.remainingUsd == null ? 'Not set' : money(opexSummary.remainingUsd) },
                  ]}
                />
              )}
              {canViewBudgets && jobSummary && (
                <BudgetCapCard
                  title={jobBudgetCardTitle(plBucket || row.costObject.plBucket)}
                  over={overBudget}
                  lines={[
                    { label: 'Budget', value: money(jobSummary.budgetUsd) },
                    { label: 'QB actual', value: money(jobSummary.actualUsd) },
                    { label: 'Reserved', value: money(jobSummary.reservedUsd) },
                    { label: 'Committed', value: money(jobSummary.committedUsd) },
                    { label: 'Remaining', value: money(jobSummary.remainingUsd) },
                  ]}
                />
              )}
              <PurchaseLineItemsEditor
                drafts={lineItemsToDrafts(row.lineItems)}
                onChange={() => undefined}
                salesTaxPct={row.salesTaxPct ?? 0}
                headerVendor={row.vendorName}
                readOnly
              />
              <p className="po-justification">
                <PurchaseLinkedText text={row.justification} />
              </p>
              <PurchaseLinksEditor
                drafts={linksToDrafts(additionalPurchaseLinks(row.links, row.lineItems))}
                onChange={() => undefined}
                readOnly
              />
              <SavedPurchaseAttachments
                files={supportingFiles}
                canWrite={canManageSupporting}
                busy={busy}
                onOpen={(href, attachment) => void openAttachmentViewer(attachment, href)}
                onAdd={next =>
                  void run(() =>
                    addPurchaseSupportingAttachments({
                      requisition: row,
                      files: next,
                      actorId,
                      access: accessArgs,
                    }),
                  )
                }
                onRemove={file =>
                  void run(() =>
                    removePurchaseAttachment({
                      requisition: row,
                      storagePath: file.storagePath,
                      actorId,
                      access: accessArgs,
                    }),
                  )
                }
              />
            </div>
          )}

          <div className="po-approval-with-fork">
          <PurchasePoForkBadge
            prNumber={row.prNumber}
            lines={row.lineItems}
            headerVendor={row.vendorName}
            headerPo={row.poNumber}
            salesTaxPct={row.salesTaxPct}
          />
          <ApprovalMap
            steps={
              row.alreadyPurchased
                ? []
                : previewNewGates
                  ? resolved.steps
                  : approvalStepsForPurchaseDisplay(row.approvalSteps, row)
            }
            origin={{
              userName: requesterLabel,
              at: row.createdAt,
              ...(row.alreadyPurchased ? { roleLabel: 'Recorded', statusVerb: 'Recorded' } : {}),
            }}
            followOn={
              row.status === 'cancelled'
                ? undefined
                : row.alreadyPurchased
                  ? {
                      roleLabel: 'Already purchased',
                      userName: purchaserLabel,
                      at: purchaseDisplayedPurchasedAt(row),
                      done: true,
                      doneStatus: 'Recorded',
                    }
                  : {
                      roleLabel: followsBill ? 'Bill payment' : 'Purchase',
                      userName: followsBill && row.status !== 'purchased' ? undefined : purchaserLabel,
                      at: purchaseDisplayedPurchasedAt(row),
                      note: row.status === 'purchased'
                        ? undefined
                        : billPaymentGuide?.stepNote || row.paymentMethodNote,
                      done: row.status === 'purchased',
                    }
            }
            bandLabel={previewNewGates ? resolved.bandLabel : row.bandLabel}
            overBudget={overBudget}
            canOverride={canOverride}
            allowSubmitOverBudget={
              overBudget
              && !canOverride
              && !budgetBlocksIssue
              && (canSubmitDraft || canResubmit)
              && !(isCurrent && awaitingApproval)
            }
          />
          </div>

          {isCurrent && row.status !== 'approved' && row.status !== 'cancelled' && row.status !== 'purchased' && (
            <div className="po-approve">
              {isFinal && (
                <>
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">Approved payment method</span>
                    <select className="fin-modal-select" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                      {issueMethods.map(m => (
                        <option key={m.id} value={m.label}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">Payment note (optional)</span>
                    <input className="fin-modal-input" value={paymentNote} onChange={e => setPaymentNote(e.target.value)} />
                  </label>
                </>
              )}
              <label className="fin-modal-field full">
                <span className="fin-modal-label">
                  {isFinal ? 'Note (optional)' : canDeclineAsApprover ? 'Note for requester (required to return or decline)' : 'Return note'}
                </span>
                <textarea
                  ref={noteFieldRef}
                  className="fin-modal-textarea"
                  rows={2}
                  value={note}
                  onChange={e => {
                    setNote(e.target.value);
                    if (noteError) setNoteError(null);
                  }}
                />
              </label>
              {noteError && <p className="po-error" role="alert">{noteError}</p>}
            </div>
          )}
          {showPurchaseForm && (
            <div className="po-approve po-actual">
              <label className="fin-modal-field">
                <span className="fin-modal-label">
                  {purchaseTarget ? `${purchaseTarget.poNumber} amount` : 'PO amount'}
                </span>
                <input className="fin-modal-input" value={money(purchaseTargetAmount)} readOnly />
              </label>
              <label className="fin-modal-field">
                <span className="fin-modal-label">Receipt / actual</span>
                <input
                  className="fin-modal-input"
                  inputMode="decimal"
                  value={actualAmountInput}
                  onChange={e => setActualAmountInput(e.target.value)}
                  aria-label="Actual purchased amount"
                />
              </label>
              <p className={`po-actual-hint${parsedActual != null && !actualWithinTol ? ' po-actual-hint--over' : ''}`}>
                    {parsedActual == null
                  ? 'Enter the receipt or card total if it differs from the PO. Within 3% you can confirm; larger changes go to your supervisor.'
                  : formatPurchaseActualVariance(
                      purchaseTargetAmount,
                      parsedActual,
                      canSelfConfirmActual && !actualWithinTol ? 'authority' : 'requester',
                    )}
              </p>
              <div className="fin-modal-field full">
                <span className="fin-modal-label">Receipt photo or PDF</span>
                {existingReceipts.length > 0 ? (
                  <div className="po-receipts">
                    {existingReceipts.map(file => (
                      <SavedAttachmentRow
                        key={file.storagePath}
                        file={file}
                        emphasize
                        canRemove={canRemoveReceipt}
                        busy={busy}
                        onOpen={(href, attachment) => void openAttachmentViewer(attachment, href)}
                        onRemove={() =>
                          void run(() =>
                            removePurchaseAttachment({
                              requisition: row,
                              storagePath: file.storagePath,
                              actorId,
                              access: accessArgs,
                            }),
                          )
                        }
                      />
                    ))}
                  </div>
                ) : null}
                {existingReceipts.length === 0 && supportingFiles.length === 0 && receiptLinks.length > 0 ? (
                  <div className="po-receipts">
                    <p className="po-actual-hint">
                      Using the link already on this request as receipt proof.
                    </p>
                    {receiptLinks.map(link => (
                      <a
                        key={link.id || link.url}
                        className="po-receipt-link"
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link.label?.trim() || link.url}
                      </a>
                    ))}
                  </div>
                ) : null}
                {existingReceipts.length === 0 && supportingFiles.length > 0 ? (
                  <div className="po-receipts">
                    <p className="po-actual-hint">
                      Using the screenshot or PDF already attached on this request as receipt proof.
                    </p>
                    {supportingFiles.map(file => (
                      <SavedAttachmentRow
                        key={file.storagePath}
                        file={file}
                        emphasize
                        canRemove={canManageSupporting}
                        busy={busy}
                        onOpen={(href, attachment) => void openAttachmentViewer(attachment, href)}
                        onRemove={() =>
                          void run(() =>
                            removePurchaseAttachment({
                              requisition: row,
                              storagePath: file.storagePath,
                              actorId,
                              access: accessArgs,
                            }),
                          )
                        }
                      />
                    ))}
                  </div>
                ) : null}
                <PendingPurchaseFiles
                  files={receiptFiles}
                  onChange={setReceiptFiles}
                  accept="image/*,.pdf"
                  addLabel={
                    receiptFiles.length || hasExistingReceiptProof
                      ? 'Add another receipt file'
                      : 'Choose receipt'
                  }
                  primaryWhenEmpty={!hasExistingReceiptProof}
                />
                {!receiptReady ? (
                  <p className="po-error" role="status">
                    Attach a receipt photo or PDF before confirming
                    {hasExistingReceiptProof
                      ? ''
                      : ' (a screenshot or link already on this request also counts)'}
                    .
                  </p>
                ) : null}
                {receiptReady && parsedActual == null ? (
                  <p className="po-actual-hint" role="status">
                    Enter the receipt total to enable Confirm purchased.
                  </p>
                ) : null}
              </div>
            </div>
          )}
          {returnNoteVisible && (
            <div className="po-approve">
              <label className="fin-modal-field full">
                <span className="fin-modal-label">Note for {requesterAccountName}</span>
                <textarea
                  ref={noteFieldRef}
                  className="fin-modal-textarea"
                  rows={2}
                  value={note}
                  onChange={e => {
                    setNote(e.target.value);
                    if (noteError) setNoteError(null);
                  }}
                />
              </label>
              {noteError && <p className="po-error" role="alert">{noteError}</p>}
            </div>
          )}
          {error && <p className="po-error">{error}</p>}
          {!returnNoteVisible && !(isCurrent && row.status !== 'approved' && row.status !== 'cancelled' && row.status !== 'purchased') && noteError && (
            <p className="po-error" role="alert">{noteError}</p>
          )}
        </div>
        <div className="fin-modal-footer">
          {canReassignRequester && requesterEditDirty && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  reassignPurchaseRequester({
                    requisition: row,
                    requesterId: requesterEditId,
                    requesterName: purchaseRequesterDirectoryName(
                      requesterEditId,
                      crmUsers,
                      row.requesterName,
                    ),
                    actorId,
                    access: accessArgs,
                  }),
                )
              }
            >
              Save requester
            </button>
          )}
          {canWithdrawCancel && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => {
                const issued = purchaseOrderNumbers(row).length > 0;
                const ok = window.confirm(
                  issued
                    ? `Cancel ${docLabel}? It will stay in Closed as cancelled.`
                    : isRequester
                      ? `Withdraw this requisition? It was not approved yet — use this if it should not have been submitted.`
                      : `Cancel ${docLabel}? It will stay in Closed as cancelled.`,
                );
                if (!ok) return;
                void run(() =>
                  cancelPurchaseRequisition({
                    requisition: row,
                    actorId,
                    actorName,
                    access: accessArgs,
                  }),
                );
              }}
            >
              {row.poNumber ? 'Cancel PO' : isRequester ? 'Withdraw request' : 'Cancel'}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="fin-danger-btn"
              disabled={busy}
              onClick={() => {
                const ok = window.confirm(`Permanently delete ${docLabel}? This cannot be undone.`);
                if (!ok) return;
                void run(async () => {
                  await deletePurchaseRequisition({
                    requisition: row,
                    actorId,
                    access: accessArgs,
                  });
                  onClose();
                });
              }}
            >
              Delete
            </button>
          )}
          {canSubmitDraft && (
            <button
              type="button"
              className="fin-primary-btn"
              disabled={busy || resolved.unresolved}
              onClick={() =>
                void run(async () => {
                  if (resolved.unresolved) throw new Error(resolved.error);
                  const steps = row.approvalSteps.some(step => step.status === 'pending')
                    ? row.approvalSteps
                    : resolved.steps;
                  await resubmitPurchaseRequisition({
                    id: row.id,
                    actorId,
                    justification: row.justification,
                    vendorName: row.vendorName,
                    lineItems: row.lineItems,
                    links: row.links,
                    salesTaxPct: row.salesTaxPct,
                    costObject: row.costObject,
                    steps,
                    bandLabel: row.bandLabel,
                    existingAttachments: row.attachments,
                    notifyTitle: 'Purchase requisition needs your approval',
                  });
                })
              }
            >
              Submit for approval
            </button>
          )}
          {canResubmit && (
            <button
              type="button"
              className="fin-primary-btn"
              disabled={busy || resolved.unresolved || budgetBlocksIssue}
              onClick={() =>
                void run(async () => {
                  const classificationError = validatePurchaseCostObject(costObject);
                  if (classificationError) throw new Error(classificationError);
                  if (resolved.unresolved) throw new Error(resolved.error);
                  if (budgetBlocksIssue) throw new Error('This amount is over the remaining budget.');
                  await resubmitPurchaseRequisition({
                    id: row.id,
                    actorId,
                    justification,
                    vendorName,
                    lineItems,
                    links: preparedPurchaseLinks(links),
                    salesTaxPct: policy.salesTaxPct,
                    costObject,
                    steps: resolved.steps,
                    bandLabel: resolved.bandLabel,
                    existingAttachments: row.attachments,
                    issueNow: !!resolved.selfAuthorized,
                    paymentMethod: resolved.selfAuthorized ? paymentMethod : undefined,
                    paymentMethodId: resolved.selfAuthorized
                      ? resolvePaymentMethodId(paymentMethod, issueMethods)
                      : undefined,
                    paymentMethodNote: resolved.selfAuthorized ? paymentNote : undefined,
                  });
                })
              }
            >
              Resubmit
            </button>
          )}
          {canHold && !row.hold && row.status !== 'purchased' && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => {
                onError(null);
                setHoldFormOpen(true);
              }}
            >
              Hold
            </button>
          )}
          {canReview && row.hold && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => void run(() => releasePurchaseHold({ requisition: row, actorId }))}
            >
              Release hold
            </button>
          )}
          {canReviseAmount && !reviseAmount && !showPurchaseForm && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => {
                onError(null);
                setReviseAmount(true);
              }}
            >
              Adjust amount
            </button>
          )}
          {reviseAmount && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => {
                setReviseAmount(false);
                setVendorName(row.vendorName || '');
                setJustification(row.justification);
                setLines(lineItemsToDrafts(row.lineItems));
                onError(null);
              }}
            >
              Back
            </button>
          )}
          {reviseAmount && (
            <button
              type="button"
              className="fin-primary-btn"
              disabled={
                busy
                || !canSubmitRevise
                || (amountEditKind === 'needs_approval' && resolved.unresolved)
                || (overBudget && !canOverride)
              }
              onClick={() =>
                void run(async () => {
                  if (!canSubmitRevise) throw new Error('The amount is unchanged.');
                  if (amountEditKind === 'needs_approval' && resolved.unresolved) throw new Error(resolved.error);
                  if (overBudget && !canOverride) throw new Error('This amount is over the remaining budget.');
                  if (amountEditKind === 'needs_approval') {
                    const ok = window.confirm(
                      'This is more than 10% off the approved amount. The PO will go back through approval and the PO number will be withdrawn.',
                    );
                    if (!ok) return;
                  }
                  const result = await reviseIssuedPurchaseAmount({
                    requisition: row,
                    actorId,
                    actorName,
                    access: accessArgs,
                    justification,
                    vendorName,
                    lineItems,
                    salesTaxPct: policy.salesTaxPct,
                    steps: resolved.steps,
                    bandLabel: resolved.bandLabel,
                    issueNow: amountEditKind === 'needs_approval' && !!resolved.selfAuthorized,
                    paymentMethod:
                      amountEditKind === 'needs_approval' && resolved.selfAuthorized ? paymentMethod : undefined,
                    paymentMethodId:
                      amountEditKind === 'needs_approval' && resolved.selfAuthorized
                        ? resolvePaymentMethodId(paymentMethod, issueMethods)
                        : undefined,
                    paymentMethodNote:
                      amountEditKind === 'needs_approval' && resolved.selfAuthorized ? paymentNote : undefined,
                  });
                  setReviseAmount(false);
                  if (result === 'updated') onError(null);
                })
              }
            >
              {amountEditKind === 'needs_approval'
                ? 'Resubmit for approval'
                : reviseStoresChanged && amountEditKind === 'unchanged'
                  ? 'Update PO'
                  : 'Update PO amount'}
            </button>
          )}
          {canManagePayables
            && followsBill
            && billPaymentGuide?.billingPayablesHref
            && row.status === 'approved'
            && !showPurchaseForm
            && !reviseAmount && (
            <Link className="fin-primary-btn" to={billPaymentGuide.billingPayablesHref} style={{ textDecoration: 'none' }}>
              Pay in Billing & Payables
            </Link>
          )}
          {canMarkBillPaidFromPo && !showPurchaseForm && !reviseAmount && (
            <button
              type="button"
              className="fin-primary-btn"
              disabled={busy || markPaidBusy}
              onClick={() =>
                void run(async () => {
                  if (!linkedSupplierBill?.id) {
                    throw new Error('Supplier bill not found for this PO.');
                  }
                  setMarkPaidBusy(true);
                  try {
                    await updateSupplierBillStatus({
                      bill: linkedSupplierBill,
                      to: 'paid',
                      byUserId: actorId,
                      byUserName: actorName || 'Office',
                      amountPaid: linkedSupplierBill.amount,
                      paymentMethod: row.paymentMethod || 'ACH',
                      note: 'Marked paid from Purchasing PO modal',
                    });
                    onError(null);
                  } finally {
                    setMarkPaidBusy(false);
                  }
                })
              }
            >
              Mark paid
            </button>
          )}
          {canRetryQbBillFromPo && !showPurchaseForm && !reviseAmount && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy || retryQbBusy}
              onClick={() =>
                void run(async () => {
                  setRetryQbBusy(true);
                  try {
                    const result = await retrySupplierBillQboBill({
                      billId: linkedSupplierBill?.id,
                      purchaseRequisitionId: row.id,
                    });
                    if (!result.success && result.status !== 'linked') {
                      throw new Error(result.message || 'QuickBooks bill retry failed.');
                    }
                    const refreshed = await findSupplierBillForPurchaseOrder(row.id);
                    setLinkedSupplierBill(refreshed);
                    onError(null);
                  } finally {
                    setRetryQbBusy(false);
                  }
                })
              }
            >
              {retryQbBusy ? 'Retrying…' : 'Retry QB bill'}
            </button>
          )}
          {canPurchase
            && followsBill
            && billPaymentHasClosePath
            && !canManagePayables
            && row.status === 'approved'
            && !showPurchaseForm
            && !reviseAmount && (
            <p className="po-issued-bill-note po-footer-bill-note">
              This PO is paid by office in Billing &amp; Payables. Upload your invoice under attachments if
              you need it on file.
            </p>
          )}
          {canPurchase
            && !multiSupplierPos
            && (!followsBill || canManualCloseBillPayment)
            && !showPurchaseForm
            && !reviseAmount && (
            <button type="button" className="fin-primary-btn" disabled={busy} onClick={() => setConfirmPurchase(true)}>
              Mark purchased
            </button>
          )}
          {showPurchaseForm && (!row.purchaseVariance || (multiSupplierPos && row.purchaseVariance.poNumber !== purchaseTarget?.poNumber)) && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => {
                setConfirmPurchase(false);
                setPurchaseTargetPo(row.purchaseVariance?.poNumber || null);
                setActualAmountInput(
                  row.purchaseVariance
                    ? row.purchaseVariance.proposedAmount.toFixed(2)
                    : row.amount.toFixed(2),
                );
                setReceiptFiles([]);
                onError(null);
              }}
            >
              Back
            </button>
          )}
          {showPurchaseForm && (actualWithinTol || canSelfConfirmActual) && (
            <button
              type="button"
              className="fin-primary-btn"
              disabled={busy || parsedActual == null || !receiptReady}
              onClick={() =>
                void run(() => {
                  if (parsedActual == null) throw new Error('Enter the receipt total.');
                  if (!receiptReady) throw new Error('Upload a receipt photo or PDF.');
                  if (multiSupplierPos && purchaseTarget) {
                    return markSupplierPurchaseOrderPurchased({
                      requisition: row,
                      poNumber: purchaseTarget.poNumber,
                      actorId,
                      actorName,
                      purchasedAmount: parsedActual,
                      authorityConfirm: !actualWithinTol && canSelfConfirmActual,
                      receiptFiles,
                      allowBillPaymentManualClose: canManualCloseBillPayment,
                    });
                  }
                  return markPurchaseRequisitionPurchased({
                    requisition: row,
                    actorId,
                    actorName,
                    purchasedAmount: parsedActual,
                    authorityConfirm: !actualWithinTol && canSelfConfirmActual,
                    receiptFiles,
                    allowBillPaymentManualClose: canManualCloseBillPayment,
                  });
                })
              }
            >
              Confirm purchased
            </button>
          )}
          {showPurchaseForm && !actualWithinTol && !canSelfConfirmActual && (
            <button
              type="button"
              className="fin-primary-btn"
              disabled={busy || parsedActual == null || !actualAuthority || !receiptReady}
              onClick={() =>
                void run(async () => {
                  if (parsedActual == null) throw new Error('Enter the receipt total.');
                  if (!receiptReady) throw new Error('Upload a receipt photo or PDF.');
                  if (!actualAuthority) throw new Error('No supervisor is assigned. Ask an admin to confirm this receipt.');
                  await requestPurchaseActualVariance({
                    requisition: row,
                    actorId,
                    actorName,
                    proposedAmount: parsedActual,
                    reviewer: actualAuthority,
                    receiptFiles,
                    poNumber: purchaseTarget?.poNumber,
                  });
                  setReceiptFiles([]);
                  setConfirmPurchase(true);
                })
              }
            >
              {actualAuthority ? `Send to ${actualAuthority.userName}` : 'No supervisor assigned'}
            </button>
          )}
          {showReturn && (
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => {
                const trimmed = note.trim();
                if (!trimmed) {
                  const msg = `Add a note so ${requesterAccountName} knows what to review.`;
                  setNoteError(msg);
                  noteFieldRef.current?.focus();
                  noteFieldRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                  return;
                }
                setNoteError(null);
                const withdrawIssued =
                  row.status === 'approved' ||
                  (row.createdViaAgent &&
                    row.alreadyPurchased &&
                    row.status === 'purchased');
                if (withdrawIssued) {
                  const ok = window.confirm(
                    `Return ${docLabel} to ${requesterAccountName} for review? The PO number(s) will be withdrawn until they resubmit (so they can change store or re-source).`,
                  );
                  if (!ok) return;
                }
                void run(() =>
                  returnPurchaseRequisition({
                    requisition: row,
                    actorId,
                    actorName,
                    note: trimmed,
                    override: approvalSubstitute || !pending,
                  }),
                );
              }}
            >
              {row.status === 'approved' || (row.createdViaAgent && !showStandardReturn)
                ? 'Return to requester'
                : 'Return for edits'}
            </button>
          )}
          {isCurrent && awaitingApproval && (
            <>
              {canDeclineAsApprover && (
                <button
                  type="button"
                  className="fin-danger-btn"
                  disabled={busy}
                  onClick={() => {
                    const trimmed = note.trim();
                    if (!trimmed) {
                      setNoteError('Add a note explaining why you are declining.');
                      noteFieldRef.current?.focus();
                      noteFieldRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                      return;
                    }
                    setNoteError(null);
                    const ok = window.confirm(
                      `Decline this requisition? It stays on record as declined (not withdrawn). ${row.requesterName || 'The requester'} will see your note.`,
                    );
                    if (!ok) return;
                    void run(() =>
                      returnPurchaseRequisition({
                        requisition: row,
                        actorId,
                        actorName,
                        note: trimmed,
                        reject: true,
                        override: approvalSubstitute,
                      }),
                    );
                  }}
                >
                  Decline
                </button>
              )}
              <button
                type="button"
                className="fin-primary-btn"
                disabled={busy || (overBudget && !canOverride)}
                onClick={() =>
                  void run(async () => {
                    if (overBudget && !canOverride) throw new Error('This amount is over the remaining budget.');
                    await approvePurchaseRequisition({
                      requisition: row,
                      actorId,
                      actorName,
                      note,
                      paymentMethod: isFinal ? paymentMethod : undefined,
                      paymentMethodId: isFinal ? resolvePaymentMethodId(paymentMethod, issueMethods) : undefined,
                      paymentMethodNote: isFinal ? paymentNote : undefined,
                      override: approvalSubstitute,
                    });
                  })
                }
              >
                {isFinal ? 'Approve & issue PO' : 'Approve'}
              </button>
            </>
          )}
        </div>
      </div>
      {holdFormOpen && canHold && !row.hold && (
        <div
          className="fin-modal-overlay po-hold-overlay"
          onClick={e => {
            e.stopPropagation();
            setHoldFormOpen(false);
          }}
        >
          <div className="fin-modal po-hold-modal" onClick={e => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Hold {docLabel}</h3>
              <button
                type="button"
                className="fin-modal-close"
                onClick={() => setHoldFormOpen(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="fin-modal-body">
              <div className="fin-modal-grid">
                <label className="fin-modal-field">
                  <span className="fin-modal-label">Hold reason</span>
                  <select
                    className="fin-modal-select"
                    value={holdReasonId}
                    onChange={e => setHoldReasonId(e.target.value)}
                  >
                    {policy.holdReasons.map(reason => (
                      <option key={reason.id} value={reason.id}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fin-modal-field">
                  <span className="fin-modal-label">Internal note (optional)</span>
                  <input className="fin-modal-input" value={holdNote} onChange={e => setHoldNote(e.target.value)} />
                </label>
                <label className="fin-modal-field full po-hold-visible">
                  <input type="checkbox" checked={holdVisible} onChange={e => setHoldVisible(e.target.checked)} />
                  <span>Show this reason to the requester</span>
                </label>
              </div>
            </div>
            <div className="fin-modal-footer">
              <button type="button" className="fin-secondary-btn" disabled={busy} onClick={() => setHoldFormOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="fin-primary-btn"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await holdPurchaseRequisition({
                      requisition: row,
                      actorId,
                      actorName,
                      reasonId: selectedHoldReason?.id || holdReasonId,
                      reasonLabel: selectedHoldReason?.label || 'On hold',
                      note: holdNote,
                      visibleToRequester: holdVisible,
                    });
                    setHoldFormOpen(false);
                  })
                }
              >
                Hold
              </button>
            </div>
          </div>
        </div>
      )}
      {attachmentViewer && (
        <Suspense fallback={null}>
          {attachmentViewer.kind === 'pdf' ? (
            <PdfViewerModal
              key={attachmentViewer.documentId}
              url={attachmentViewer.url}
              title={attachmentViewer.title}
              documentId={attachmentViewer.documentId}
              leadId={PURCHASING_VIEWER_LEAD_ID}
              leadName={row.vendorName || row.title || 'Purchase order'}
              projectId={timberProjectId}
              dealId={dealId}
              disableTakeoff
              initialAnnotationId={attachmentViewer.initialAnnotationId}
              initialPageNumber={attachmentViewer.initialPageNumber}
              onClose={closeAttachmentViewer}
            />
          ) : (
            <ImageViewerModal
              key={attachmentViewer.documentId}
              url={attachmentViewer.url}
              title={attachmentViewer.title}
              documentId={attachmentViewer.documentId}
              leadId={PURCHASING_VIEWER_LEAD_ID}
              leadName={row.vendorName || row.title || 'Purchase order'}
              projectId={timberProjectId}
              dealId={dealId}
              mimeType={attachmentViewer.mimeType}
              initialAnnotationId={attachmentViewer.initialAnnotationId}
              onClose={closeAttachmentViewer}
            />
          )}
        </Suspense>
      )}
    </div>
  );
}

function purchaseReceiptPreviewKind(file: PurchaseAttachment): 'pdf' | 'image' | null {
  const type = (file.contentType || '').toLowerCase();
  const name = (file.name || file.storagePath || '').toLowerCase();
  if (type.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/i.test(name)) return 'image';
  return null;
}

const PURCHASE_DOC_ACCEPT = 'image/*,.pdf,application/pdf';

function PendingPurchaseFiles({
  files,
  onChange,
  addLabel,
  accept = PURCHASE_DOC_ACCEPT,
  primaryWhenEmpty = false,
}: {
  files: File[];
  onChange: (next: File[]) => void;
  addLabel: string;
  accept?: string;
  primaryWhenEmpty?: boolean;
}) {
  return (
    <div className="po-files">
      {files.length > 0 ? (
        <ul className="po-file-list">
          {files.map((file, index) => (
            <li key={`${file.name}-${file.size}-${file.lastModified}-${index}`} className="po-file-row">
              <span className="po-file-link po-file-link--pending">
                <FileText size={14} aria-hidden />
                <span>{file.name}</span>
              </span>
              <button
                type="button"
                className="po-file-remove"
                aria-label={`Remove ${file.name}`}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <label className={`po-receipt-add${primaryWhenEmpty && files.length === 0 ? ' po-receipt-add--primary' : ''}`}>
        <input
          type="file"
          accept={accept}
          multiple
          className="po-file-hidden"
          onChange={e => {
            const next = Array.from(e.target.files || []);
            e.target.value = '';
            if (!next.length) return;
            onChange([...files, ...next]);
          }}
        />
        {addLabel}
      </label>
    </div>
  );
}

function SavedAttachmentRow({
  file,
  emphasize,
  canRemove,
  busy,
  onOpen,
  onRemove,
}: {
  file: PurchaseAttachment;
  emphasize?: boolean;
  canRemove?: boolean;
  busy?: boolean;
  onOpen?: (href: string, file: PurchaseAttachment) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="po-file-row">
      <AttachmentLink file={file} emphasize={emphasize} onOpen={onOpen} />
      {canRemove ? (
        <button
          type="button"
          className="po-file-remove"
          aria-label={`Remove ${file.name}`}
          disabled={busy}
          onClick={onRemove}
        >
          <Trash2 size={13} />
        </button>
      ) : null}
    </div>
  );
}

function SavedPurchaseAttachments({
  files,
  canWrite,
  busy,
  onOpen,
  onAdd,
  onRemove,
}: {
  files: PurchaseAttachment[];
  canWrite: boolean;
  busy?: boolean;
  onOpen?: (href: string, file: PurchaseAttachment) => void;
  onAdd: (files: File[]) => void;
  onRemove: (file: PurchaseAttachment) => void;
}) {
  if (!canWrite && files.length === 0) return null;
  return (
    <div className="po-files">
      <span className="fin-modal-label">Screenshots / quote docs</span>
      {files.length > 0 ? (
        <ul className="po-file-list">
          {files.map(file => (
            <li key={file.storagePath}>
              <SavedAttachmentRow
                file={file}
                canRemove={canWrite}
                busy={busy}
                onOpen={onOpen}
                onRemove={() => onRemove(file)}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {canWrite ? (
        <label className={`po-receipt-add${files.length === 0 ? ' po-receipt-add--primary' : ''}`}>
          <input
            type="file"
            accept={PURCHASE_DOC_ACCEPT}
            multiple
            disabled={busy}
            className="po-file-hidden"
            onChange={e => {
              const next = Array.from(e.target.files || []);
              e.target.value = '';
              if (!next.length) return;
              onAdd(next);
            }}
          />
          {files.length ? 'Add another screenshot' : 'Add screenshots or PDFs'}
        </label>
      ) : null}
    </div>
  );
}

function AttachmentLink({
  file,
  emphasize,
  onOpen,
}: {
  file: PurchaseAttachment;
  emphasize?: boolean;
  onOpen?: (href: string, file: PurchaseAttachment) => void;
}) {
  const [href, setHref] = useState(file.url || '');

  useEffect(() => {
    if (file.url) {
      setHref(file.url);
      return;
    }
    if (!file.storagePath) return;
    let cancelled = false;
    getDownloadURL(storageRef(storage, file.storagePath))
      .then(url => {
        if (!cancelled) setHref(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [file.url, file.storagePath]);

  const body = (
    <>
      <FileText size={14} aria-hidden />
      <span>{file.name}</span>
    </>
  );

  if (href) {
    if (onOpen) {
      return (
        <button
          type="button"
          className={emphasize ? 'po-receipt-link' : 'po-file-link'}
          onClick={e => {
            e.stopPropagation();
            onOpen(href, file);
          }}
        >
          {body}
        </button>
      );
    }
    return (
      <a
        className={emphasize ? 'po-receipt-link' : 'po-file-link'}
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={e => e.stopPropagation()}
      >
        {body}
      </a>
    );
  }

  return <span className={emphasize ? 'po-receipt-link po-receipt-link--pending' : 'po-file-link'}>{body}</span>;
}

export default PurchasingPage;
